<template>
  <div class="page-wrap" :class="{ night }">
    <AppHeader />

    <div class="toolbar">
      <div class="pos-title">{{ trendTitle }}</div>
      <div class="chart-tabs">
        <button
          v-for="tab in visibleChartTabs"
          :key="tab.key"
          class="chart-tab"
          :class="{ on: chartType === tab.key }"
          type="button"
          @click="chartType = tab.key"
        >
          {{ tab.label }}
        </button>
        <button
          v-if="chartType === 'line'"
          class="gear-btn"
          type="button"
          aria-label="设置"
          @click="openSheet('settings')"
        >
          <svg viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.2 7.2 0 0 0-1.63-.94l-.36-2.54A.5.5 0 0 0 13.9 2h-3.8a.5.5 0 0 0-.49.42l-.36 2.54c-.59.22-1.14.53-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.71 8.84a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94L2.83 14.52a.5.5 0 0 0-.12.64l1.92 3.32c.14.24.43.34.7.22l2.39-.96c.49.4 1.04.72 1.63.94l.36 2.54c.05.24.26.42.49.42h3.8c.23 0 .44-.18.49-.42l.36-2.54c.59-.22 1.14-.53 1.63-.94l2.39.96c.27.12.56.02.7-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58ZM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2Z"
            />
          </svg>
        </button>
      </div>
    </div>

    <div v-if="chartType === 'kline'" class="pos-row" :class="{ 'pos-row-pk10': isPk10, 'pos-row-center': isK3 }">
      <template v-if="isPk10">
        <div class="pos-row-line">
          <button
            v-for="p in positions.slice(0, 7)"
            :key="'kpos' + p.index"
            class="pos-tab"
            :class="{ on: position === p.index }"
            type="button"
            @click="pickPosition(p.index)"
          >
            {{ p.name }}
          </button>
        </div>
        <div class="pos-row-line pos-row-line-center">
          <button
            v-for="p in positions.slice(7)"
            :key="'kpos' + p.index"
            class="pos-tab"
            :class="{ on: position === p.index }"
            type="button"
            @click="pickPosition(p.index)"
          >
            {{ p.name }}
          </button>
        </div>
      </template>
      <template v-else>
        <button
          v-for="p in positions"
          :key="'kpos' + p.index"
          class="pos-tab"
          :class="{ on: position === p.index }"
          type="button"
          @click="pickPosition(p.index)"
        >
          {{ p.name }}
        </button>
      </template>
    </div>

    <div
      v-show="chartType === 'line'"
      ref="scrollRef"
      class="table-scroll"
      :class="{
        'p3p5-scroll': isP3p5Complex || isP3p5Triple || isP3p5Pair || isD3All || isD3Sum || isK3Complex || isK3SumForm,
        'dlt-h-scroll': isLfWideTrend || isDltForm || isSsqForm
      }"
    >
      <table v-if="isPk10Dt" class="dt-table">
        <thead>
          <tr>
            <th class="dt-col-issue" @click="newestFirst = !newestFirst">
              期号
              <i class="sort-caret" :class="{ up: !newestFirst }" />
            </th>
            <th class="dt-col-code">开奖号码</th>
            <th
              v-for="(pair, pi) in pk10DtPairs"
              :key="'dt-h' + pi"
              class="dt-col-dt"
            >
              <span>{{ pair.head[0] }}</span>
              <small>{{ pair.head[1] }}</small>
              <span>{{ pair.head[2] }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in pk10DtDisplayRows" :key="row.issue" :class="{ alt: i % 2 === 1 }">
            <td class="dt-col-issue">
              <span class="dt-issue-text">{{ row.issue }}</span>
            </td>
            <td class="dt-col-code">
              <div class="dt-code-scale">
                <span
                  v-for="(n, idx) in row.codes"
                  :key="`${row.issue}-${idx}`"
                  class="pk10-ball"
                  :style="pk10BallStyle(n)"
                >{{ pk10BallText(n) }}</span>
              </div>
            </td>
            <td
              v-for="(res, ri) in row.dt"
              :key="`${row.issue}-dt-${ri}`"
              class="dt-col-dt"
              :class="res === '龙' ? 'dragon' : 'tiger'"
            >{{ res }}</td>
          </tr>
        </tbody>
      </table>
      <table v-else-if="isPk10Gy" class="dt-table gy-table">
        <thead>
          <tr>
            <th class="dt-col-issue" @click="newestFirst = !newestFirst">
              期号
              <i class="sort-caret" :class="{ up: !newestFirst }" />
            </th>
            <th class="dt-col-code">开奖号码</th>
            <th colspan="2" class="gy-col-sum">冠亚和大小单双</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in pk10GyDisplayRows" :key="row.issue" :class="{ alt: i % 2 === 1 }">
            <td class="dt-col-issue">
              <span class="dt-issue-text">{{ row.issue }}</span>
            </td>
            <td class="dt-col-code">
              <div class="dt-code-scale">
                <span
                  v-for="(n, idx) in row.codes"
                  :key="`${row.issue}-${idx}`"
                  class="pk10-ball"
                  :style="pk10BallStyle(n)"
                >{{ pk10BallText(n) }}</span>
              </div>
            </td>
            <td
              class="gy-col-bs"
              :class="row.bs === '大' ? 'big' : 'small'"
            >{{ row.bs }}</td>
            <td
              class="gy-col-oe"
              :class="row.oe === '单' ? 'odd' : 'even'"
            >{{ row.oe }}</td>
          </tr>
        </tbody>
      </table>
      <table v-else-if="isPk10Fantan" class="dt-table ft-table">
        <thead>
          <tr>
            <th class="dt-col-issue" @click="newestFirst = !newestFirst">
              期号
              <i class="sort-caret" :class="{ up: !newestFirst }" />
            </th>
            <th class="dt-col-code">开奖号码</th>
            <th class="ft-col-gufan">孤番</th>
            <th class="ft-col-bs">番大小</th>
            <th class="ft-col-oe">番单双</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in pk10FantanDisplayRows" :key="row.issue" :class="{ alt: i % 2 === 1 }">
            <td class="dt-col-issue">
              <span class="dt-issue-text">{{ row.issue }}</span>
            </td>
            <td class="dt-col-code">
              <div class="dt-code-scale">
                <span
                  v-for="(n, idx) in row.codes"
                  :key="`${row.issue}-${idx}`"
                  class="pk10-ball"
                  :style="pk10BallStyle(n)"
                >{{ pk10BallText(n) }}</span>
              </div>
            </td>
            <td class="ft-col-gufan">{{ row.guFan }}</td>
            <td class="ft-col-bs" :class="row.bs === '大' ? 'big' : 'small'">{{ row.bs }}</td>
            <td class="ft-col-oe" :class="row.oe === '单' ? 'odd' : 'even'">{{ row.oe }}</td>
          </tr>
        </tbody>
      </table>
      <table v-else-if="isPk10RankBsOe" class="dt-table f5-table">
        <thead>
          <tr>
            <th class="dt-col-issue" @click="newestFirst = !newestFirst">
              期号
              <i class="sort-caret" :class="{ up: !newestFirst }" />
            </th>
            <th
              v-for="label in pk10RankBsOeLabels"
              :key="'f5-h' + label"
              class="f5-col-rank"
            >{{ label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in pk10RankBsOeDisplayRows" :key="row.issue" :class="{ alt: i % 2 === 1 }">
            <td class="dt-col-issue">
              <span class="dt-issue-text">{{ row.issue }}</span>
            </td>
            <td
              v-for="(cell, ri) in row.ranks"
              :key="`${row.issue}-r-${ri}`"
              class="f5-col-rank"
            >
              <span class="f5-bs" :class="cell.bs === '大' ? 'big' : 'small'">{{ cell.bs }}</span>
              <span class="f5-oe" :class="cell.oe === '单' ? 'odd' : 'even'">{{ cell.oe }}</span>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else-if="isP3p5Pair" class="table-inner p3-first2-inner">
        <table class="p3-first2-table">
          <thead>
            <tr>
              <th class="col-issue" @click="newestFirst = !newestFirst">
                期号
                <i class="sort-caret" :class="{ up: !newestFirst }" />
              </th>
              <th class="col-code">开奖号码</th>
              <th class="col-bs">大小</th>
              <th class="col-oe">单双</th>
              <th class="col-pc">质合</th>
              <th class="col-sum">和值</th>
              <th class="col-span">跨度</th>
              <th class="col-pair">对子</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in p3p5PairDisplayRows" :key="row.issue" :class="{ alt: i % 2 === 1 }">
              <td class="col-issue">{{ row.issue }}</td>
              <td class="col-code">
                <span class="code-line">{{ row.codes.join(' ') }}</span>
              </td>
              <td class="col-bs">
                <span
                  v-for="(t, ti) in row.bs"
                  :key="'bs2-' + row.issue + ti"
                  class="type-chip"
                  :class="t === '大' ? 'big' : 'small'"
                >{{ t }}</span>
              </td>
              <td class="col-oe">
                <span
                  v-for="(t, ti) in row.oe"
                  :key="'oe2-' + row.issue + ti"
                  class="type-chip"
                  :class="t === '单' ? 'odd' : 'even'"
                >{{ t }}</span>
              </td>
              <td class="col-pc">
                <span
                  v-for="(t, ti) in row.pc"
                  :key="'pc2-' + row.issue + ti"
                  class="type-chip"
                  :class="t === '质' ? 'prime' : 'composite'"
                >{{ t }}</span>
              </td>
              <td class="col-sum">{{ row.sum }}</td>
              <td class="col-span">{{ row.span }}</td>
              <td class="col-pair" :class="{ 'hit-pair': row.isPair }">
                <span v-if="row.isPair" class="form-check zu3" />
                <span v-else-if="showMiss" class="miss-num">{{ row.pairMiss }}</span>
              </td>
            </tr>
            <template v-if="showStats">
              <tr v-for="stat in statRows" :key="'p3p2-' + stat.key" class="stat-row">
                <td colspan="2" class="stat-label">{{ stat.label }}</td>
                <td class="stat-dash">-</td>
                <td class="stat-dash">-</td>
                <td class="stat-dash">-</td>
                <td class="stat-dash">-</td>
                <td class="stat-dash">-</td>
                <td class="stat-cell">{{ p3p5PairStats[stat.key].pair }}</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div v-else-if="isD3Sum" class="table-inner d3-sum-inner">
        <table class="d3-sum-table">
          <thead>
            <tr>
              <th rowspan="2" class="col-issue" @click="newestFirst = !newestFirst">
                期号
                <i class="sort-caret" :class="{ up: !newestFirst }" />
              </th>
              <th rowspan="2" class="col-code">开奖号码</th>
              <th colspan="2" class="col-group">百十</th>
              <th colspan="2" class="col-group">百个</th>
              <th colspan="2" class="col-group">十个</th>
              <th colspan="2" class="col-group">百十个</th>
            </tr>
            <tr>
              <th class="col-sub">和数</th>
              <th class="col-sub">和尾</th>
              <th class="col-sub">和数</th>
              <th class="col-sub">合尾</th>
              <th class="col-sub">和数</th>
              <th class="col-sub">和尾</th>
              <th class="col-sub">和数</th>
              <th class="col-sub">和尾</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in d3SumDisplayRows" :key="row.issue" :class="{ alt: i % 2 === 1 }">
              <td class="col-issue">{{ row.issue }}</td>
              <td class="col-code">
                <span class="code-line">{{ row.codes.join(' ') }}</span>
              </td>
              <td class="col-num">{{ row.btSum }}</td>
              <td class="col-num">{{ row.btTail }}</td>
              <td class="col-num">{{ row.bgSum }}</td>
              <td class="col-num">{{ row.bgTail }}</td>
              <td class="col-num">{{ row.sgSum }}</td>
              <td class="col-num">{{ row.sgTail }}</td>
              <td class="col-num">{{ row.allSum }}</td>
              <td class="col-num">{{ row.allTail }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="isDltForm" class="table-inner dlt-form-inner">
        <table class="dlt-form-table dlt-form-wide">
          <thead>
            <tr>
              <th class="col-issue" @click="newestFirst = !newestFirst">
                期号
                <i class="sort-caret" :class="{ up: !newestFirst }" />
              </th>
              <th class="col-code">开奖号码</th>
              <th class="col-num">前区和</th>
              <th class="col-num">前跨度</th>
              <th class="col-num">前奇偶</th>
              <th class="col-num">前大小</th>
              <th class="col-num">后区和</th>
              <th class="col-num">后奇偶</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in dltFormDisplayRows" :key="row.issue" :class="{ alt: i % 2 === 1 }">
              <td class="col-issue">{{ row.issue }}</td>
              <td class="col-code">
                <span
                  v-for="(n, idx) in row.codes"
                  :key="idx"
                  class="code-n"
                  :class="{ 'dlt-back': idx >= 5 }"
                >{{ formatTrendBall(n) }}</span>
              </td>
              <td class="col-num">{{ row.frontSum }}</td>
              <td class="col-num">{{ row.frontSpan }}</td>
              <td class="col-num">{{ row.frontOe }}</td>
              <td class="col-num">{{ row.frontBs }}</td>
              <td class="col-num">{{ row.backSum }}</td>
              <td class="col-num">{{ row.backOe }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="isSsqForm" class="table-inner dlt-form-inner">
        <table class="dlt-form-table dlt-form-wide">
          <thead>
            <tr>
              <th class="col-issue" @click="newestFirst = !newestFirst">
                期号
                <i class="sort-caret" :class="{ up: !newestFirst }" />
              </th>
              <th class="col-code">开奖号码</th>
              <th class="col-num">红球和</th>
              <th class="col-num">红跨度</th>
              <th class="col-num">红奇偶</th>
              <th class="col-num">红大小</th>
              <th class="col-num">蓝大小</th>
              <th class="col-num">蓝单双</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in ssqFormDisplayRows" :key="row.issue" :class="{ alt: i % 2 === 1 }">
              <td class="col-issue">{{ row.issue }}</td>
              <td class="col-code">
                <span
                  v-for="(n, idx) in row.codes"
                  :key="idx"
                  class="code-n"
                  :class="{ 'dlt-back': idx === 6 }"
                >{{ formatTrendBall(n) }}</span>
              </td>
              <td class="col-num">{{ row.redSum }}</td>
              <td class="col-num">{{ row.redSpan }}</td>
              <td class="col-num">{{ row.redOe }}</td>
              <td class="col-num">{{ row.redBs }}</td>
              <td class="col-num">{{ row.blueBs }}</td>
              <td class="col-num">{{ row.blueOe }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="isD3All" class="table-inner p3-first3-inner d3-all-inner">
        <table class="p3-first3-table d3-all-table">
          <thead>
            <tr>
              <th class="col-issue" @click="newestFirst = !newestFirst">
                期号
                <i class="sort-caret" :class="{ up: !newestFirst }" />
              </th>
              <th class="col-code">开奖号码</th>
              <th class="col-bs">大小</th>
              <th class="col-oe">单双</th>
              <th class="col-pc">质合</th>
              <th class="col-sum">和值</th>
              <th class="col-form">组三</th>
              <th class="col-form">组六</th>
              <th class="col-form">豹子</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in d3AllDisplayRows" :key="row.issue" :class="{ alt: i % 2 === 1 }">
              <td class="col-issue">{{ row.issue }}</td>
              <td class="col-code">
                <span class="code-line">{{ (row.triple || row.codes).join(' ') }}</span>
              </td>
              <td class="col-bs">
                <span
                  v-for="(t, ti) in row.bs"
                  :key="'d3bs-' + row.issue + ti"
                  class="type-chip"
                  :class="t === '大' ? 'big' : 'small'"
                >{{ t }}</span>
              </td>
              <td class="col-oe">
                <span
                  v-for="(t, ti) in row.oe"
                  :key="'d3oe-' + row.issue + ti"
                  class="type-chip"
                  :class="t === '单' ? 'odd' : 'even'"
                >{{ t }}</span>
              </td>
              <td class="col-pc">
                <span
                  v-for="(t, ti) in row.pc"
                  :key="'d3pc-' + row.issue + ti"
                  class="type-chip"
                  :class="t === '质' ? 'prime' : 'composite'"
                >{{ t }}</span>
              </td>
              <td class="col-sum">{{ row.sum }}</td>
              <td class="col-form" :class="{ 'hit-zu3': row.form === 'zu3' }">
                <span v-if="row.form === 'zu3'" class="form-check zu3" />
                <span v-else-if="showMiss" class="miss-num">{{ row.zu3Miss }}</span>
              </td>
              <td class="col-form" :class="{ 'hit-zu6': row.form === 'zu6' }">
                <span v-if="row.form === 'zu6'" class="form-check zu6" />
                <span v-else-if="showMiss" class="miss-num">{{ row.zu6Miss }}</span>
              </td>
              <td class="col-form" :class="{ 'hit-baozi': row.form === 'baozi' }">
                <span v-if="row.form === 'baozi'" class="form-check baozi" />
                <span v-else-if="showMiss" class="miss-num">{{ row.baoziMiss }}</span>
              </td>
            </tr>
            <template v-if="showStats">
              <tr v-for="stat in statRows" :key="'d3all-' + stat.key" class="stat-row">
                <td colspan="2" class="stat-label">{{ stat.label }}</td>
                <td class="stat-dash">-</td>
                <td class="stat-dash">-</td>
                <td class="stat-dash">-</td>
                <td class="stat-dash">-</td>
                <td class="stat-cell">{{ d3AllStats[stat.key].zu3 }}</td>
                <td class="stat-cell">{{ d3AllStats[stat.key].zu6 }}</td>
                <td class="stat-cell">{{ d3AllStats[stat.key].baozi }}</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div v-else-if="isP3p5Triple" class="table-inner p3-first3-inner">
        <table class="p3-first3-table">
          <thead>
            <tr>
              <th class="col-issue" @click="newestFirst = !newestFirst">
                期号
                <i class="sort-caret" :class="{ up: !newestFirst }" />
              </th>
              <th class="col-code">开奖号码</th>
              <th class="col-bs">大小</th>
              <th class="col-oe">单双</th>
              <th class="col-pc">质合</th>
              <th class="col-form">组三</th>
              <th class="col-form">组六</th>
              <th class="col-form">豹子</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in p3p5TripleDisplayRows" :key="row.issue" :class="{ alt: i % 2 === 1 }">
              <td class="col-issue">{{ row.issue }}</td>
              <td class="col-code">
                <span class="code-line">{{ row.codes.join(' ') }}</span>
              </td>
              <td class="col-bs">
                <span
                  v-for="(t, ti) in row.bs"
                  :key="'bs-' + row.issue + ti"
                  class="type-chip"
                  :class="t === '大' ? 'big' : 'small'"
                >{{ t }}</span>
              </td>
              <td class="col-oe">
                <span
                  v-for="(t, ti) in row.oe"
                  :key="'oe-' + row.issue + ti"
                  class="type-chip"
                  :class="t === '单' ? 'odd' : 'even'"
                >{{ t }}</span>
              </td>
              <td class="col-pc">
                <span
                  v-for="(t, ti) in row.pc"
                  :key="'pc-' + row.issue + ti"
                  class="type-chip"
                  :class="t === '质' ? 'prime' : 'composite'"
                >{{ t }}</span>
              </td>
              <td class="col-form" :class="{ 'hit-zu3': row.form === 'zu3' }">
                <span v-if="row.form === 'zu3'" class="form-check zu3" />
                <span v-else-if="showMiss" class="miss-num">{{ row.zu3Miss }}</span>
              </td>
              <td class="col-form" :class="{ 'hit-zu6': row.form === 'zu6' }">
                <span v-if="row.form === 'zu6'" class="form-check zu6" />
                <span v-else-if="showMiss" class="miss-num">{{ row.zu6Miss }}</span>
              </td>
              <td class="col-form" :class="{ 'hit-baozi': row.form === 'baozi' }">
                <span v-if="row.form === 'baozi'" class="form-check baozi" />
                <span v-else-if="showMiss" class="miss-num">{{ row.baoziMiss }}</span>
              </td>
            </tr>
            <template v-if="showStats">
              <tr v-for="stat in statRows" :key="'p3t-' + stat.key" class="stat-row">
                <td colspan="2" class="stat-label">{{ stat.label }}</td>
                <td class="stat-dash">-</td>
                <td class="stat-dash">-</td>
                <td class="stat-dash">-</td>
                <td class="stat-cell">{{ p3p5TripleStats[stat.key].zu3 }}</td>
                <td class="stat-cell">{{ p3p5TripleStats[stat.key].zu6 }}</td>
                <td class="stat-cell">{{ p3p5TripleStats[stat.key].baozi }}</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div v-else-if="isK3SumForm" ref="innerRef" class="table-inner k3-sum-inner">
        <table class="k3-sum-table">
          <thead>
            <tr>
              <th class="col-issue" @click="newestFirst = !newestFirst">
                期号
                <i class="sort-caret" :class="{ up: !newestFirst }" />
              </th>
              <th class="col-code">开奖号码</th>
              <th class="col-form">对子<br><small>(二同号)</small></th>
              <th class="col-form">组六<br><small>(三不同)</small></th>
              <th class="col-form">豹子<br><small>(三同号)</small></th>
              <th class="col-bsoe">大小单双</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in k3SumDisplayRows"
              :key="row.issue"
              :class="{ alt: i % 2 === 1 }"
            >
              <td class="col-issue">{{ row.issue }}</td>
              <td class="col-code">
                <span
                  v-for="(n, idx) in row.codes"
                  :key="idx"
                  class="mini-dice"
                >
                  <i
                    v-for="(dot, di) in diceDots(n)"
                    :key="`${idx}-${di}`"
                    class="mdot"
                    :class="[dot.p, { red: dot.red }]"
                  />
                </span>
              </td>
              <td class="col-form">
                <span v-if="row.form === 'zu3'" class="form-check pair" />
                <span v-else-if="showMiss" class="miss-num">{{ row.pairMiss }}</span>
              </td>
              <td class="col-form">
                <span v-if="row.form === 'zu6'" class="form-check zu6" />
                <span v-else-if="showMiss" class="miss-num">{{ row.zu6Miss }}</span>
              </td>
              <td class="col-form">
                <span v-if="row.form === 'baozi'" class="form-check baozi" />
                <span v-else-if="showMiss" class="miss-num">{{ row.baoziMiss }}</span>
              </td>
              <td class="col-bsoe">
                <span class="bsoe-chip" :class="row.sumBs === '大' ? 'big' : 'small'">{{ row.sumBs }}</span>
                <span class="bsoe-chip" :class="row.sumOe === '单' ? 'odd' : 'even'">{{ row.sumOe }}</span>
              </td>
            </tr>
            <template v-if="showStats">
              <tr v-for="stat in k3SumStatMeta" :key="'k3sum-' + stat.key" class="stat-row">
                <td colspan="2" class="stat-label">{{ stat.label }}</td>
                <td class="stat-cell">{{ k3SumStats[stat.key].pair }}</td>
                <td class="stat-cell">{{ k3SumStats[stat.key].zu6 }}</td>
                <td class="stat-cell">{{ k3SumStats[stat.key].baozi }}</td>
                <td class="stat-dash">-</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div v-else-if="isK3Complex" ref="innerRef" class="table-inner k3-complex-inner">
        <table class="k3-complex-table">
          <thead>
            <tr>
              <th class="col-issue" @click="newestFirst = !newestFirst">
                期号
                <i class="sort-caret" :class="{ up: !newestFirst }" />
              </th>
              <th class="col-code">开奖号码</th>
              <th class="col-ball">第一球</th>
              <th class="col-ball">第一球</th>
              <th class="col-ball">第二球</th>
              <th class="col-ball">第二球</th>
              <th class="col-ball">第三球</th>
              <th class="col-ball">第三球</th>
              <th class="col-span-val">跨度</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in k3ComplexDisplayRows"
              :key="row.issue"
              :class="{ alt: i % 2 === 1 }"
            >
              <td class="col-issue">{{ row.issue }}</td>
              <td class="col-code">
                <span
                  v-for="(n, idx) in row.codes"
                  :key="idx"
                  class="mini-dice"
                >
                  <i
                    v-for="(dot, di) in diceDots(n)"
                    :key="`${idx}-${di}`"
                    class="mdot"
                    :class="[dot.p, { red: dot.red }]"
                  />
                </span>
              </td>
              <template v-for="(ball, bi) in row.balls" :key="row.issue + '-b' + bi">
                <td class="col-attr" :class="ball.bs === '大' ? 'big' : 'small'">{{ ball.bs }}</td>
                <td class="col-attr" :class="ball.oe === '单' ? 'odd' : 'even'">{{ ball.oe }}</td>
              </template>
              <td class="col-span-val">{{ row.span }}</td>
            </tr>
            <template v-if="showStats">
              <tr v-for="stat in statRows" :key="'k3cx-' + stat.key" class="stat-row">
                <td class="stat-label" colspan="2">{{ stat.label }}</td>
                <td
                  v-for="(v, si) in k3ComplexStats[stat.key]"
                  :key="stat.key + si"
                  class="stat-cell"
                >{{ v }}</td>
                <td class="stat-dash">-</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div v-else-if="isP3p5Complex" ref="innerRef" class="table-inner p3p5-complex-inner">
        <table class="p3p5-complex-table">
          <thead>
            <tr>
              <th rowspan="2" class="col-issue" @click="newestFirst = !newestFirst">
                期号
                <i class="sort-caret" :class="{ up: !newestFirst }" />
              </th>
              <th rowspan="2" class="col-code">开奖号码</th>
              <th rowspan="2" class="col-sum">和值</th>
              <th colspan="6" class="col-group">大小比</th>
              <th colspan="6" class="col-group">单双比</th>
            </tr>
            <tr>
              <th v-for="key in ratioKeys" :key="'bs-h' + key" class="col-ratio">{{ key }}</th>
              <th v-for="key in ratioKeys" :key="'oe-h' + key" class="col-ratio">{{ key }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in p3p5ComplexDisplayRows" :key="row.issue" :class="{ alt: i % 2 === 1 }">
              <td class="col-issue">{{ row.issue }}</td>
              <td class="col-code">
                <span class="code-line">{{ row.codes.join(' ') }}</span>
              </td>
              <td class="col-sum">{{ row.sum }}</td>
              <td
                v-for="(key, di) in ratioKeys"
                :key="'bs-' + row.issue + key"
                class="col-ratio"
              >
                <span v-if="row.bsHit === di" class="ratio-hit-label ratio-hit-bs">{{ row.bsRatio }}</span>
                <span v-else-if="showMiss" class="miss-num">{{ row.bsMiss[di] }}</span>
              </td>
              <td
                v-for="(key, di) in ratioKeys"
                :key="'oe-' + row.issue + key"
                class="col-ratio"
              >
                <span v-if="row.oeHit === di" class="ratio-hit-label ratio-hit-oe">{{ row.oeRatio }}</span>
                <span v-else-if="showMiss" class="miss-num">{{ row.oeMiss[di] }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else ref="innerRef" class="table-inner">
        <table
          class="trend-table"
          :class="{ wide: ballSize > 10 || isLfWideTrend, k3: isK3, dlt: isLfWide }"
          :style="{ '--ball-cols': ballSize, '--cell-w': isLfWide && ballSize > 20 ? '22px' : (isK3 ? '28px' : '28px'), '--fix-w': isK3 ? 'clamp(112px, 36vw, 146px)' : (isLfWide ? '210px' : '164px') }"
        >
          <thead>
            <tr>
              <th rowspan="2" class="col-issue" @click="newestFirst = !newestFirst">
                期号
                <i class="sort-caret" :class="{ up: !newestFirst }" />
              </th>
              <th rowspan="2" class="col-code" :class="{ k3: isK3, dlt: isLfWide }">开奖号码</th>
              <th :colspan="ballSize" class="col-span">{{ filterLabel }}走势</th>
            </tr>
            <tr>
              <th v-for="d in digits" :key="'h' + d" class="col-d">{{ formatTrendBall(d) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in displayRows" :key="row.issue" :class="{ alt: i % 2 === 1 }">
              <td class="col-issue">{{ row.issue }}</td>
              <td class="col-code" :class="{ k3: isK3, dlt: isLfWide }">
                <template v-if="isK3">
                  <span
                    v-for="(n, idx) in row.codes.slice(0, 3)"
                    :key="idx"
                    class="mini-dice"
                    :class="{ cur: codeCur(idx) }"
                  >
                    <i
                      v-for="(dot, di) in diceDots(n)"
                      :key="`${idx}-${di}`"
                      class="mdot"
                      :class="[dot.p, { red: dot.red }]"
                    />
                  </span>
                </template>
                <template v-else>
                  <span
                    v-for="(n, idx) in row.codes"
                    :key="idx"
                    class="code-n"
                    :class="{ cur: codeCur(idx), 'dlt-back': isLfBlueBall(idx) }"
                  >{{ formatTrendBall(n) }}</span>
                </template>
              </td>
              <td
                v-for="(d, di) in digits"
                :key="d"
                class="col-d"
                :class="{ hit: rowHits(row).includes(d) }"
              >
                <span v-if="rowHits(row).includes(d)" class="hit-ball">{{ d }}</span>
                <span v-else-if="showMiss" class="miss-num">{{ row.miss[di] }}</span>
              </td>
            </tr>
            <template v-if="showStats">
              <tr v-for="stat in statRows" :key="stat.key" class="stat-row">
                <td colspan="2" class="stat-label">{{ stat.label }}</td>
                <td v-for="(d, di) in digits" :key="stat.key + d" class="col-d stat-cell">
                  {{ stats[stat.key][di] }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <svg
          v-if="showLine && !isMultiHit && linePoints.length > 1"
          class="trend-svg"
          :style="svgStyle"
        >
          <polyline
            :points="linePoints"
            fill="none"
            stroke="#ff7a18"
            stroke-width="1.4"
            stroke-linejoin="round"
            stroke-linecap="round"
          />
        </svg>
      </div>
    </div>

    <div v-show="chartType === 'kline'" ref="kPaneRef" class="k-pane">
      <svg
        class="k-svg"
        :viewBox="`0 0 ${kChart.W} ${kChart.H}`"
        preserveAspectRatio="none"
      >
        <line
          v-for="t in kChart.yTicks"
          :key="'hg' + t.d"
          :x1="kChart.pad.l"
          :x2="kChart.W - kChart.pad.r"
          :y1="t.y"
          :y2="t.y"
          stroke="#ececec"
          stroke-width="1"
        />
        <rect
          :x="kChart.pad.l"
          :y="kChart.pad.t"
          :width="kChart.W - kChart.pad.l - kChart.pad.r"
          :height="kChart.plotBottom - kChart.pad.t"
          fill="none"
          stroke="#e5e5e5"
          stroke-width="1"
        />
        <text
          v-for="t in kChart.yTicks"
          :key="'yl' + t.d"
          :x="kChart.pad.l - 6"
          :y="t.y + 3.5"
          text-anchor="end"
          class="k-y-label"
        >{{ t.d }}</text>
        <polyline
          :points="kChart.line"
          fill="none"
          stroke="#58b9c1"
          stroke-width="2"
          stroke-linejoin="round"
          stroke-linecap="round"
        />
        <circle
          v-for="p in kChart.points"
          :key="'pt' + p.issue"
          :cx="p.x"
          :cy="p.y"
          r="4"
          fill="#fff"
          stroke="#58b9c1"
          stroke-width="2"
        />
        <text
          v-for="p in kChart.points.filter((item) => item.showLabel)"
          :key="'xl' + p.issue"
          :x="p.x"
          :y="kChart.plotBottom + 10"
          text-anchor="start"
          class="k-x-label"
          :transform="`rotate(42 ${p.x} ${kChart.plotBottom + 10})`"
        >{{ p.issue }}</text>
      </svg>
    </div>

    <div v-show="chartType === 'hist'" ref="histPaneRef" class="hist-pane">
      <svg
        class="hist-svg"
        :viewBox="`0 0 ${histChart.W} ${histChart.H}`"
        preserveAspectRatio="xMidYMid meet"
      >
        <rect
          :x="histChart.pad.l"
          :y="histChart.pad.t"
          :width="histChart.plotRight - histChart.pad.l"
          :height="histChart.plotBottom - histChart.pad.t"
          :fill="histTheme.plotFill"
          :stroke="histTheme.plotStroke"
          stroke-width="1"
        />
        <line
          v-for="line in histChart.hLines"
          :key="'hh' + line.i"
          :x1="histChart.pad.l"
          :x2="histChart.plotRight"
          :y1="line.y"
          :y2="line.y"
          :stroke="histTheme.grid"
          stroke-width="1"
        />
        <line
          v-for="tick in histChart.xTicks"
          :key="'hv' + tick.v"
          :x1="tick.x"
          :x2="tick.x"
          :y1="histChart.pad.t"
          :y2="histChart.plotBottom"
          :stroke="histTheme.grid"
          stroke-width="1"
        />
        <rect
          v-for="bar in histChart.bars"
          :key="'bar' + bar.d"
          :x="bar.x"
          :y="bar.y"
          :width="bar.w"
          :height="bar.h"
          :fill="histTheme.barFill"
          :stroke="histTheme.barStroke"
          stroke-width="1"
        />
        <text
          v-for="bar in histChart.bars"
          :key="'hy' + bar.d"
          :x="histChart.pad.l - 8"
          :y="bar.labelY"
          text-anchor="end"
          class="hist-axis"
        >{{ bar.d }}</text>
        <text
          v-for="tick in histChart.xTicks"
          :key="'hx' + tick.v"
          :x="tick.x"
          :y="histChart.plotBottom + 16"
          text-anchor="middle"
          class="hist-axis"
        >{{ tick.v }}</text>
      </svg>
    </div>

    <button
      v-show="!sheet"
      class="fab"
      type="button"
      aria-label="下载开奖数据"
      :disabled="downloading"
      @click="downloadDrawHistory"
    >
      <svg viewBox="0 0 24 24">
        <path
          fill="#fff"
          d="M12 16.5 6.5 11l1.4-1.4 3.1 3.1V4h2v8.7l3.1-3.1L17.5 11 12 16.5ZM6 20v-2h12v2H6Z"
        />
      </svg>
    </button>

    <footer class="foot-bar">
      <button type="button" @click="openSheet('period')">
        {{ periodLabel }}
        <i class="caret" />
      </button>
      <button type="button" @click="openSheet('lottery')">
        {{ currentGame.name }}
        <i class="caret" />
      </button>
      <button type="button" @click="openSheet('help')">走势图说明</button>
    </footer>

    <div
      v-if="sheet"
      class="mask"
      :class="{ 'lot-mask': sheet === 'lottery', 'help-mask': sheet === 'help', 'filter-mask': sheet === 'settings' }"
      @click="sheet = ''"
    >
      <div v-if="sheet === 'lottery'" class="lot-panel" @click.stop>
        <div class="lot-scroll">
          <section class="lot-acc">
            <button class="lot-head" type="button" @click="toggleAcc('fav')">
              <img class="lot-dice" :src="lotBoxIcon" alt="" />
              收藏彩种选择
              <span class="lot-chev" :class="{ open: openAcc === 'fav' }" aria-hidden="true">
                <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5" fill="none" stroke="#c0c0c0" stroke-width="1.4"/><path d="M8.2 10.4 12 14.2l3.8-3.8" fill="none" stroke="#b0b0b0" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </span>
            </button>
            <div v-show="openAcc === 'fav'" class="lot-body">
              <button
                v-for="game in favPickerGames"
                :key="'fav' + game.id"
                class="lot-game"
                :class="{ on: gameId === game.id }"
                type="button"
                @click="pickLottery(game.id)"
              >
                {{ game.name }}
              </button>
            </div>
          </section>

          <section v-for="cat in visibleCats" :key="cat.key" class="lot-acc">
            <button class="lot-head" type="button" @click="toggleAcc(cat.key)">
              <img class="lot-dice" :src="lotBoxIcon" alt="" />
              {{ cat.label }}
              <span class="lot-chev" :class="{ open: openAcc === cat.key }" aria-hidden="true">
                <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8.5" fill="none" stroke="#c0c0c0" stroke-width="1.4"/><path d="M8.2 10.4 12 14.2l3.8-3.8" fill="none" stroke="#b0b0b0" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </span>
            </button>
            <div v-show="openAcc === cat.key" class="lot-body">
              <button
                v-for="game in gamesForLotCat(cat)"
                :key="game.id"
                class="lot-game"
                :class="{ on: gameId === game.id }"
                type="button"
                @click="pickLottery(game.id)"
              >
                {{ game.name }}
              </button>
            </div>
          </section>
        </div>
        <button class="lot-cancel" type="button" @click="sheet = ''">取消</button>
      </div>

      <div v-else-if="sheet === 'help'" class="help-dialog" @click.stop>
        <div class="help-title">走势图使用说明</div>
        <div class="help-body">
          <section class="help-sec">
            <h2>综合分析</h2>
            <div class="help-sec-title">多号走势：</div>
            <div class="help-sec-text">根据每期每位置提供全面即时高级走势图表，快速捕捉状态节省分析时间。</div>
            <div class="help-sec-title">K线图：</div>
            <div class="help-sec-text">提供开奖号码或指标值在每期的变化情况，以K折线形式表示偏度值、散度值。</div>
            <ul class="help-sub">
              <li>K线图中，横坐标表示期号，纵坐标表示该期出现的奖号值。</li>
              <li>鼠标指向圆点时会弹出相应期号及对应球奖号的提示框。</li>
            </ul>
            <div class="help-sec-title">直方图：</div>
            <div class="help-sec-text">统计某阶段内，各号码或指标值出现次数统计方便分析近期号码的开出频率。</div>
            <ul class="help-sub">
              <li>直方图中，横坐标表示要统计的基本号码，纵坐标表示每个基本号码在设定期数范围内的出现次数。</li>
              <li>鼠标移到柱状图上时会弹出相应基本号码及对应出现次数的提示框。</li>
            </ul>
            <div class="help-sec-title">历史开奖数据：</div>
            <div class="help-sec-text">提供当前期号、开奖号码、开奖时间，更直接方便快速查找开奖号。</div>
          </section>

          <section class="help-sec">
            <h2>功能说明</h2>
            <div class="help-sec-title">折 线：</div>
            <div class="help-sec-text">选中时将会在表格中绘制开奖号码的走势。</div>
            <div class="help-sec-title">遗 漏：</div>
            <div class="help-sec-text">选中时开奖号码的表格中显示出该号码的遗漏值。</div>
            <div class="help-sec-title">辅助线：</div>
            <div class="help-sec-text">每五期使用分隔线，辅助数据分隔更加清晰。</div>
            <div class="help-sec-title">遗 漏：</div>
            <div class="help-sec-text">表格中将会把遗漏期使用有色的柱图来表示。</div>
          </section>

          <section class="help-sec">
            <h2>统计说明</h2>
            <div class="help-sec-title">遗漏数据：</div>
            <div class="help-sec-text">自上期开出到本期间隔的期数。</div>
            <div class="help-sec-title">出现总次数：</div>
            <div class="help-sec-text">统计期数内实际出现的次数。</div>
            <div class="help-sec-title">平均遗漏：</div>
            <div class="help-sec-text">
              统计期数内遗漏的平均值。<br />
              计算公式：平均遗漏 = 统计期数 / 出现次数 + 1
            </div>
            <div class="help-sec-title">最大遗漏：</div>
            <div class="help-sec-text">统计期数内遗漏的最大值。</div>
            <div class="help-sec-title">最大连出值：</div>
            <div class="help-sec-text">统计期数内连续开出的最大值。</div>
          </section>

          <section class="help-sec">
            <h2>查询数据</h2>
            <div class="help-subtitle">
              查询最近<span>30</span>期、最近<span>50</span>期、最近<span>100</span>期。
            </div>
          </section>
        </div>
        <button class="help-close" type="button" @click="sheet = ''">关闭</button>
      </div>

      <div v-else-if="sheet === 'settings'" class="filter-dialog" @click.stop>
        <div class="filter-head">
          筛选条件
          <button class="filter-x" type="button" aria-label="关闭" @click="sheet = ''">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" fill="none" stroke="#fff" stroke-width="1.6" />
              <path d="M8.2 8.2l7.6 7.6M15.8 8.2l-7.6 7.6" fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round" />
            </svg>
          </button>
        </div>
        <div class="filter-body">
          <section v-if="positions.length" class="filter-sec">
            <h3>定位走势</h3>
            <div class="filter-grid" :style="filterGridStyle(positions.length)">
              <button
                v-for="p in positions"
                :key="'fp' + p.index"
                class="filter-chip"
                :class="{ on: trendKind === 'single' && position === p.index }"
                type="button"
                @click="pickPosition(p.index)"
              >
                {{ p.name }}
              </button>
            </div>
          </section>
          <section v-if="comboOptions.length" class="filter-sec">
            <h3>组合走势</h3>
            <div class="filter-grid" :style="filterGridStyle(comboOptions.length)">
              <button
                v-for="key in comboOptions"
                :key="key"
                class="filter-chip"
                :class="{ on: trendKind === 'combo' && comboKey === key }"
                type="button"
                @click="pickCombo(key)"
              >
                {{ comboLabels[key] }}
              </button>
            </div>
          </section>
          <section v-for="sec in extraSections" :key="sec.title" class="filter-sec">
            <h3>{{ sec.title }}</h3>
            <div class="filter-grid" :style="filterGridStyle(sec.items.length)">
              <button
                v-for="item in sec.items"
                :key="item.key"
                class="filter-chip"
                :class="{ on: trendKind === 'extra' && extraKey === item.key }"
                type="button"
                @click="pickExtra(item.key)"
              >
                {{ item.name }}
              </button>
            </div>
          </section>
        </div>
      </div>

      <div v-else class="sheet" @click.stop>
        <template v-if="sheet === 'period'">
          <div class="sheet-title">选择期数</div>
          <button
            v-for="opt in periodOptions"
            :key="opt.value"
            class="sheet-item"
            :class="{ on: period === opt.value }"
            type="button"
            @click="pickPeriod(opt.value)"
          >
            {{ opt.label }}
            <span v-if="period === opt.value" class="check">✓</span>
          </button>
        </template>

        <template v-else-if="sheet === 'position'">
          <div class="sheet-title">选择位置</div>
          <button
            v-for="p in positions"
            :key="p.index"
            class="sheet-item"
            :class="{ on: trendKind === 'single' && position === p.index }"
            type="button"
            @click="pickPosition(p.index)"
          >
            定位走势-{{ p.name }}
            <span v-if="trendKind === 'single' && position === p.index" class="check">✓</span>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppHeader from '@/components/AppHeader.vue'
import lotBoxIcon from '@/assets/lobby/ui/lot-box.svg'
import { games, favPickerList, gamesByGroup as listGamesByGroup, visibleLotCats, syncGameVisibility, LOT_SHEET_CATS, gamesForLotCat } from '@/data/games'
import { useTheme } from '@/composables/useTheme'
import { useActiveGame } from '@/composables/useActiveGame'
import {
  CHART_TABS,
  PERIOD_OPTIONS,
  STAT_ROWS,
  buildTrendRows,
  computeStats,
  generateDraws,
  getComboOptions,
  getDigitLen,
  getPositions,
  getTrendSetting,
  COMBO_LABELS,
  COMBO_RANGE,
  isMultiCombo,
  isSumCombo,
  PK10_DT_PAIRS,
  calcPk10DragonTiger,
  calcPk10GyBsOe,
  calcPk10Front5BsOe,
  calcPk10Back5BsOe,
  calcPk10Fantan,
  PK10_FRONT5_RANKS,
  PK10_BACK5_RANKS,
  buildDrawDownloadText,
  parseDrawDownloadText,
  P3P5_RATIO_KEYS,
  buildP3p5ComplexRows,
  buildP3First3Rows,
  computeP3First3Stats,
  buildP3First2Rows,
  computeP3First2Stats,
  buildD3SumDigitRows,
  buildDltFormRows,
  buildSsqFormRows,
  buildK3ComplexRows,
  computeK3ComplexStats,
  buildK3SumRows,
  computeK3SumStats
} from '@/data/trend'
import { pk10BallColor } from '@/data/bet'
import { lgwApi } from '@/api/lgw'

const chartTabs = CHART_TABS
const periodOptions = PERIOD_OPTIONS
const statRows = STAT_ROWS
const { night } = useTheme()
const histTheme = computed(() =>
  night.value
    ? {
        plotFill: '#000',
        plotStroke: '#333',
        grid: '#1f1f1f',
        barFill: '#2a5555',
        barStroke: '#4a9e9e'
      }
    : {
        plotFill: '#fff',
        plotStroke: '#ddd',
        grid: '#eee',
        barFill: '#b5e4e2',
        barStroke: '#7ec8c4'
      }
)
const route = useRoute()
const router = useRouter()
const { gameId, currentGame, setActiveGame, ensureGameQuery } = useActiveGame()
ensureGameQuery(route, router)

const period = ref(30)
const liveDraws = ref([])
const downloading = ref(false)
const position = ref(0)
const trendKind = ref('single')
const comboKey = ref('')
const extraKey = ref('')
const chartType = ref('line')
const newestFirst = ref(true)
const showMiss = ref(true)
const showLine = ref(true)
const showStats = ref(true)
const sheet = ref('')
const openAcc = ref('fav')
const linePoints = ref('')
const bsLinePoints = ref('')
const oeLinePoints = ref('')
const svgStyle = ref({ top: '0px', height: '0px', width: '0px' })

const scrollRef = ref(null)
const innerRef = ref(null)
const kPaneRef = ref(null)
const histPaneRef = ref(null)
const kSize = ref({ w: 360, h: 340 })
let ro = null
let kRo = null
let lineRaf = 0
let kRaf = 0

const lotCats = LOT_SHEET_CATS

const favPickerGames = computed(() => favPickerList())
const visibleCats = computed(() => visibleLotCats(lotCats))
const gamesByGroup = (key) => listGamesByGroup(key)

const trendSetting = computed(() => getTrendSetting(currentGame.value))
const isPk10 = computed(() => currentGame.value?.group === 'PK10')
const isPk10Dt = computed(() => isPk10.value && trendKind.value === 'extra' && extraKey.value === 'dt')
const isPk10Gy = computed(() => isPk10.value && trendKind.value === 'extra' && extraKey.value === 'gy')
const isPk10Front5 = computed(() => isPk10.value && trendKind.value === 'extra' && extraKey.value === 'front5')
const isPk10Back5 = computed(() => isPk10.value && trendKind.value === 'extra' && extraKey.value === 'back5')
const isPk10Fantan = computed(() => isPk10.value && trendKind.value === 'extra' && extraKey.value === 'fantan')
const isPk10RankBsOe = computed(() => isPk10Front5.value || isPk10Back5.value)
const isPk10ExtraLine = computed(() => isPk10Dt.value || isPk10Gy.value || isPk10RankBsOe.value || isPk10Fantan.value)
const pk10RankBsOeLabels = computed(() => (
  isPk10Back5.value ? PK10_BACK5_RANKS : PK10_FRONT5_RANKS
))
const pk10DtPairs = PK10_DT_PAIRS
const visibleChartTabs = computed(() => (
  isPk10ExtraLine.value ? CHART_TABS.filter((tab) => tab.key !== 'hist') : CHART_TABS
))
const isK3 = computed(() => currentGame.value?.group === 'K3' || Number(currentGame.value?.id) === 1909)
const isSumMode = computed(() => trendKind.value === 'combo' && isSumCombo(comboKey.value))
const isMultiHit = computed(() => trendKind.value === 'combo' && isMultiCombo(comboKey.value))
const isP3p5Complex = computed(() =>
  trendSetting.value.key === 'P3P5' &&
  trendKind.value === 'combo' &&
  comboKey.value === 'All_5_Complex' &&
  chartType.value === 'line'
)
const isK3Complex = computed(() =>
  isK3.value &&
  trendKind.value === 'combo' &&
  comboKey.value === 'K3_All' &&
  chartType.value === 'line'
)
const isK3SumForm = computed(() =>
  isK3.value &&
  trendKind.value === 'combo' &&
  comboKey.value === 'K3_Sum' &&
  chartType.value === 'line'
)
const isP3p5Triple = computed(() =>
  trendSetting.value.key === 'P3P5' &&
  trendKind.value === 'combo' &&
  (comboKey.value === 'P3_First_3' || comboKey.value === 'P5_Last_3') &&
  chartType.value === 'line'
)
const isD3All = computed(() =>
  trendSetting.value.key === 'D3' &&
  trendKind.value === 'combo' &&
  comboKey.value === 'D3_All' &&
  chartType.value === 'line'
)
const isD3Sum = computed(() =>
  trendSetting.value.key === 'D3' &&
  trendKind.value === 'combo' &&
  comboKey.value === 'D3_Sum' &&
  chartType.value === 'line'
)
const isDltForm = computed(() =>
  trendSetting.value.key === 'DLT' &&
  trendKind.value === 'combo' &&
  comboKey.value === 'DLT_Form' &&
  chartType.value === 'line'
)
const isDlt = computed(() => trendSetting.value.key === 'DLT')
/** 大乐透号码走势（定位 / 前区 / 后区）：宽表左右滑，形态除外 */
const isDltDigitTrend = computed(() =>
  isDlt.value &&
  chartType.value === 'line' &&
  !isDltForm.value
)
const isSsqForm = computed(() =>
  trendSetting.value.key === 'SSQ' &&
  trendKind.value === 'combo' &&
  comboKey.value === 'SSQ_Form' &&
  chartType.value === 'line'
)
const isSsq = computed(() => trendSetting.value.key === 'SSQ')
/** 双色球号码走势（定位 / 红球 / 蓝球）：宽表左右滑，形态除外 */
const isSsqDigitTrend = computed(() =>
  isSsq.value &&
  chartType.value === 'line' &&
  !isSsqForm.value
)
/** 低频宽表（大乐透 / 双色球）共用 sticky 左右滑样式 */
const isLfWide = computed(() => isDlt.value || isSsq.value)
const isLfWideTrend = computed(() => isDltDigitTrend.value || isSsqDigitTrend.value)
const isP3p5Pair = computed(() =>
  trendKind.value === 'combo' &&
  chartType.value === 'line' &&
  (
    (trendSetting.value.key === 'P3P5' && (comboKey.value === 'P3_First_2' || comboKey.value === 'P5_Last_2')) ||
    (trendSetting.value.key === 'D3' && (comboKey.value === 'D3_First_2' || comboKey.value === 'D3_Last_2'))
  )
)
const ratioKeys = P3P5_RATIO_KEYS
const digitLen = computed(() => trendSetting.value.digitLen || getDigitLen(currentGame.value))
const activePosMeta = computed(() =>
  (positions.value || []).find((p) => p.index === position.value) || null
)
const ballMin = computed(() => {
  if (isSumMode.value) {
    if (comboKey.value === 'K3_Sum') return 3
    if (comboKey.value === 'PCB_Sum') return 0
  }
  if (isDlt.value) {
    if (trendKind.value === 'combo' && comboKey.value === 'DLT_Back') return 1
    if (trendKind.value === 'combo' && comboKey.value === 'DLT_Front') return 1
    if (activePosMeta.value?.ballMin != null) return Number(activePosMeta.value.ballMin)
  }
  if (isSsq.value) {
    if (trendKind.value === 'combo' && comboKey.value === 'SSQ_Blue') return 1
    if (trendKind.value === 'combo' && comboKey.value === 'SSQ_Red') return 1
    if (activePosMeta.value?.ballMin != null) return Number(activePosMeta.value.ballMin)
  }
  return trendSetting.value.ballMin ?? 0
})
const ballSize = computed(() => {
  if (isSumMode.value) {
    if (comboKey.value === 'K3_Sum') return 16
    if (comboKey.value === 'PCB_Sum') return 28
  }
  if (isDlt.value) {
    if (trendKind.value === 'combo' && comboKey.value === 'DLT_Back') return 12
    if (trendKind.value === 'combo' && comboKey.value === 'DLT_Front') return 35
    if (trendKind.value === 'combo' && comboKey.value === 'DLT_Form') return 1
    const min = Number(activePosMeta.value?.ballMin ?? trendSetting.value.ballMin ?? 1)
    const max = Number(activePosMeta.value?.ballMax ?? trendSetting.value.ballMax ?? 35)
    return Math.max(1, max - min + 1)
  }
  if (isSsq.value) {
    if (trendKind.value === 'combo' && comboKey.value === 'SSQ_Blue') return 16
    if (trendKind.value === 'combo' && comboKey.value === 'SSQ_Red') return 33
    if (trendKind.value === 'combo' && comboKey.value === 'SSQ_Form') return 1
    const min = Number(activePosMeta.value?.ballMin ?? trendSetting.value.ballMin ?? 1)
    const max = Number(activePosMeta.value?.ballMax ?? trendSetting.value.ballMax ?? 33)
    return Math.max(1, max - min + 1)
  }
  return (trendSetting.value.ballMax ?? 9) - (trendSetting.value.ballMin ?? 0) + 1
})
const digits = computed(() => Array.from({ length: ballSize.value }, (_, i) => ballMin.value + i))
const positions = computed(() => trendSetting.value.positions || getPositions(digitLen.value))
const comboLabels = COMBO_LABELS
const comboOptions = computed(() => trendSetting.value.combos || getComboOptions(currentGame.value))
const extraSections = computed(() => trendSetting.value.extras || [])
const extraName = computed(() => {
  for (const sec of extraSections.value) {
    const item = sec.items.find((it) => it.key === extraKey.value)
    if (!item) continue
    return item.name === sec.title ? item.name : `${sec.title}-${item.name}`
  }
  return ''
})
const positionName = computed(() => {
  const found = positions.value.find((p) => p.index === position.value)
  return found ? found.name : positions.value[0]?.name || '万位'
})
const comboName = computed(() => COMBO_LABELS[comboKey.value] || '')
const filterLabel = computed(() => {
  if (trendKind.value === 'combo' && comboName.value) return comboName.value
  if (trendKind.value === 'extra' && extraName.value) return extraName.value
  return positionName.value
})
const trendTitle = computed(() => {
  if (trendKind.value === 'combo' && comboName.value) return `组合走势-${comboName.value}`
  if (trendKind.value === 'extra' && extraName.value) {
    for (const sec of extraSections.value) {
      const item = sec.items.find((it) => it.key === extraKey.value)
      if (item) return `${sec.title}-${item.name}`
    }
    return extraName.value
  }
  return `定位走势-${positionName.value}`
})
const periodLabel = computed(() => {
  const found = periodOptions.find((p) => p.value === period.value)
  return found ? found.label : `最近${period.value}期`
})

const TREND_DOWNLOAD_FILE_CODES = {
  19503: 'TCG1FPK10',
  10403: 'TCG1FPK10',
  1802: 'TCSPK10',
  16: 'DLT',
  17: 'SSQ',
  14: 'FC3D'
}

const downloadFileName = computed(() => {
  const code = TREND_DOWNLOAD_FILE_CODES[gameId.value] || `${currentGame.value?.group}_${gameId.value}`
  return `${code} history draw number.txt`
})

const filterGridStyle = (n) => {
  const cols = Math.min(4, Math.max(Number(n) || 1, 1))
  return { gridTemplateColumns: `repeat(${cols}, 1fr)` }
}

function rowHits (row) {
  if (Array.isArray(row.wins) && row.wins.length) return row.wins
  return row.win != null ? [row.win] : []
}

function codeCur (idx) {
  // 福彩3D 定位走势：开奖号三位同色灰色，不标红当前位（对齐参考图）
  if (trendSetting.value.key === 'D3' && trendKind.value === 'single') return false
  if (trendKind.value === 'single') return idx === position.value
  if (trendKind.value === 'combo') {
    const range = COMBO_RANGE[comboKey.value]
    if (!range) return false
    const maxIdx = Math.max(0, digitLen.value - 1)
    return idx >= range[0] && idx <= Math.min(range[1], maxIdx)
  }
  return false
}

function formatTrendBall (n) {
  if (isLfWide.value || trendSetting.value.key === '11X5' || trendSetting.value.key === 'PK10') {
    return String(Number(n) || 0).padStart(2, '0')
  }
  return n
}

function isLfBlueBall (idx) {
  if (isSsq.value) return idx === 6
  if (isDlt.value) return idx >= 5
  return false
}

function diceDots (n) {
  const v = ((Number(n) - 1) % 6 + 6) % 6 + 1
  const map = {
    1: [{ p: 'c', red: true }],
    2: [{ p: 'lt' }, { p: 'rb' }],
    3: [{ p: 'lt' }, { p: 'c' }, { p: 'rb' }],
    4: [
      { p: 'lt', red: true },
      { p: 'rt', red: true },
      { p: 'lb', red: true },
      { p: 'rb', red: true }
    ],
    5: [{ p: 'lt' }, { p: 'rt' }, { p: 'c' }, { p: 'lb' }, { p: 'rb' }],
    6: [{ p: 'lt' }, { p: 'lm' }, { p: 'lb' }, { p: 'rt' }, { p: 'rm' }, { p: 'rb' }]
  }
  return map[v] || map[1]
}

const mockDraws = computed(() =>
  generateDraws({
    gameId: currentGame.value.id,
    digitLen: digitLen.value,
    count: period.value,
    group: currentGame.value.group
  })
)

const normalizeDrawRows = (page) => {
  const rows = page?.list || page?.content || page?.rows || []
  if (!Array.isArray(rows)) return []
  return rows.map((row) => ({
    issue: String(row.numero || row.issue || ''),
    codes: Array.isArray(row.codes) ? row.codes.map((n) => Number(n) || 0) : []
  })).filter((r) => r.issue && r.codes.length)
}

const loadDraws = async () => {
  const gid = gameId.value
  if (gid == null) return
  try {
    const res = await lgwApi.draws(gid, period.value)
    if (res?.success && res.data) {
      const list = normalizeDrawRows(res.data)
      if (list.length) liveDraws.value = list
    }
  } catch (_) {}
}

const draws = computed(() => {
  const need = period.value
  const live = liveDraws.value
  if (!live.length) return mockDraws.value
  if (live.length >= need) return live.slice(0, need)

  // 接口返回不足所选期数时，用本地生成数据补齐，保证切换 30/50/100 有可见变化
  const seen = new Set(live.map((d) => d.issue))
  const out = live.slice()
  for (const row of mockDraws.value) {
    if (out.length >= need) break
    if (seen.has(row.issue)) continue
    out.push(row)
    seen.add(row.issue)
  }
  return out
})

const pk10DtRows = computed(() =>
  draws.value.map((draw) => ({
    issue: draw.issue,
    codes: draw.codes || [],
    dt: calcPk10DragonTiger(draw.codes)
  }))
)
const pk10DtDisplayRows = computed(() =>
  newestFirst.value ? pk10DtRows.value : [...pk10DtRows.value].reverse()
)

const pk10GyRows = computed(() =>
  draws.value.map((draw) => {
    const { bs, oe } = calcPk10GyBsOe(draw.codes)
    return {
      issue: draw.issue,
      codes: draw.codes || [],
      bs,
      oe
    }
  })
)
const pk10GyDisplayRows = computed(() =>
  newestFirst.value ? pk10GyRows.value : [...pk10GyRows.value].reverse()
)

const pk10FantanRows = computed(() =>
  draws.value.map((draw) => {
    const ft = calcPk10Fantan(draw.codes)
    return {
      issue: draw.issue,
      codes: draw.codes || [],
      guFan: ft.guFan,
      bs: ft.bs,
      oe: ft.oe
    }
  })
)
const pk10FantanDisplayRows = computed(() =>
  newestFirst.value ? pk10FantanRows.value : [...pk10FantanRows.value].reverse()
)

const pk10RankBsOeRows = computed(() => {
  const calc = isPk10Back5.value ? calcPk10Back5BsOe : calcPk10Front5BsOe
  return draws.value.map((draw) => ({
    issue: draw.issue,
    ranks: calc(draw.codes)
  }))
})
const pk10RankBsOeDisplayRows = computed(() =>
  newestFirst.value ? pk10RankBsOeRows.value : [...pk10RankBsOeRows.value].reverse()
)

const pk10BallText = (n) => String(Number(n) || 0).padStart(2, '0')
const pk10BallStyle = (n) => ({ background: pk10BallColor(Number(n)) })

const trendRows = computed(() => {
  const range = COMBO_RANGE[comboKey.value]
  const maxIdx = Math.max(0, digitLen.value - 1)
  if (isSumMode.value && range) {
    const [from, to] = range
    const end = Math.min(to, maxIdx)
    return buildTrendRows(draws.value, position.value, {
      ballMin: ballMin.value,
      size: ballSize.value,
      winOf: (d) => (d.codes || []).slice(from, end + 1).reduce((a, b) => a + Number(b || 0), 0)
    })
  }
  if (isMultiHit.value && range) {
    const [from, to] = range
    const end = Math.min(to, maxIdx)
    return buildTrendRows(draws.value, position.value, {
      ballMin: ballMin.value,
      size: ballSize.value,
      winsOf: (d) => (d.codes || []).slice(from, end + 1).map(Number)
    })
  }
  return buildTrendRows(draws.value, position.value, {
    ballMin: ballMin.value,
    size: ballSize.value
  })
})
const displayRows = computed(() =>
  newestFirst.value ? trendRows.value : [...trendRows.value].reverse()
)
const p3p5ComplexRows = computed(() => buildP3p5ComplexRows(draws.value))
const p3p5ComplexDisplayRows = computed(() =>
  newestFirst.value ? p3p5ComplexRows.value : [...p3p5ComplexRows.value].reverse()
)
const k3ComplexRows = computed(() => buildK3ComplexRows(draws.value))
const k3ComplexDisplayRows = computed(() =>
  newestFirst.value ? k3ComplexRows.value : [...k3ComplexRows.value].reverse()
)
const k3ComplexStats = computed(() => computeK3ComplexStats(k3ComplexRows.value))
const k3SumStatMeta = [
  { key: 'appear', label: '出现总次数' },
  { key: 'avgMiss', label: '平均遗漏值' },
  { key: 'maxMiss', label: '最大遗漏值' },
  { key: 'maxStreak', label: '最大连出值' }
]
const k3SumRows = computed(() => buildK3SumRows(draws.value))
const k3SumDisplayRows = computed(() =>
  newestFirst.value ? k3SumRows.value : [...k3SumRows.value].reverse()
)
const k3SumStats = computed(() => computeK3SumStats(k3SumRows.value))
const p3p5TripleRows = computed(() => {
  const range = COMBO_RANGE[comboKey.value] || [0, 2]
  return buildP3First3Rows(draws.value, range)
})
const p3p5TripleDisplayRows = computed(() =>
  newestFirst.value ? p3p5TripleRows.value : [...p3p5TripleRows.value].reverse()
)
const p3p5TripleStats = computed(() => computeP3First3Stats(p3p5TripleRows.value))
const d3AllRows = computed(() => buildP3First3Rows(draws.value, [0, 2]))
const d3AllDisplayRows = computed(() =>
  newestFirst.value ? d3AllRows.value : [...d3AllRows.value].reverse()
)
const d3AllStats = computed(() => computeP3First3Stats(d3AllRows.value))
const d3SumRows = computed(() => buildD3SumDigitRows(draws.value))
const d3SumDisplayRows = computed(() =>
  newestFirst.value ? d3SumRows.value : [...d3SumRows.value].reverse()
)
const dltFormRows = computed(() => buildDltFormRows(draws.value))
const dltFormDisplayRows = computed(() =>
  newestFirst.value ? dltFormRows.value : [...dltFormRows.value].reverse()
)
const ssqFormRows = computed(() => buildSsqFormRows(draws.value))
const ssqFormDisplayRows = computed(() =>
  newestFirst.value ? ssqFormRows.value : [...ssqFormRows.value].reverse()
)
const p3p5PairRows = computed(() => {
  const range = COMBO_RANGE[comboKey.value] || [0, 1]
  return buildP3First2Rows(draws.value, range)
})
const p3p5PairDisplayRows = computed(() =>
  newestFirst.value ? p3p5PairRows.value : [...p3p5PairRows.value].reverse()
)
const p3p5PairStats = computed(() => computeP3First2Stats(p3p5PairRows.value))
const stats = computed(() => computeStats(trendRows.value, ballSize.value))

const histChart = computed(() => {
  const counts = Array(ballSize.value).fill(0)
  draws.value.forEach((draw) => {
    draw.codes.forEach((n) => {
      const slot = n - ballMin.value
      if (slot >= 0 && slot < ballSize.value) counts[slot] += 1
    })
  })
  const W = kSize.value.w
  const H = kSize.value.h
  const pad = { l: 32, r: 14, t: 8, b: 32 }
  const plotW = Math.max(1, W - pad.l - pad.r)
  const plotH = Math.max(1, H - pad.t - pad.b)
  const maxCount = Math.max(...counts, 1)
  const xMax = Math.max(25, Math.ceil(maxCount / 5) * 5)
  const band = plotH / ballSize.value
  const barH = Math.max(14, band * 0.7)
  const bars = counts.map((count, slot) => {
    const y = pad.t + slot * band + (band - barH) / 2
    return {
      d: ballMin.value + slot,
      count,
      x: pad.l,
      y,
      w: count ? Math.max(3, (count / xMax) * plotW) : 0,
      h: barH,
      labelY: pad.t + slot * band + band / 2 + 4
    }
  })
  const xTicks = []
  for (let v = 0; v <= xMax; v += 5) {
    xTicks.push({ v, x: pad.l + (v / xMax) * plotW })
  }
  const hLines = Array.from({ length: ballSize.value + 1 }, (_, i) => ({
    i,
    y: pad.t + i * band
  }))
  return {
    W,
    H,
    pad,
    plotBottom: pad.t + plotH,
    plotRight: pad.l + plotW,
    bars,
    xTicks,
    hLines
  }
})

const kChart = computed(() => {
  // K 线始终按定位位号码绘制（与例图一致：青线 + 白心圆点，Y 从 0 到最大号）
  const yMax = Number(trendSetting.value.ballMax ?? 9)
  const yMin = 0
  const rows = draws.value.map((draw) => ({
    issue: draw.issue,
    win: Number(draw.codes?.[position.value] ?? yMin)
  }))
  const W = Math.max(kSize.value.w, 120)
  const H = Math.max(kSize.value.h, 200)
  const pad = { l: 28, r: 12, t: 12, b: 58 }
  const plotW = Math.max(1, W - pad.l - pad.r)
  const plotH = Math.max(1, H - pad.t - pad.b)
  const n = rows.length || 1
  const span = Math.max(1, yMax - yMin)
  const yOf = (v) => {
    const clamped = Math.min(yMax, Math.max(yMin, Number(v) || 0))
    return pad.t + ((yMax - clamped) / span) * plotH
  }
  const xOf = (i) => (n === 1 ? pad.l + plotW / 2 : pad.l + (i / (n - 1)) * plotW)
  const step = n > 50 ? 5 : n > 20 ? 2 : 1
  const yTicks = []
  for (let d = yMax; d >= yMin; d -= 1) yTicks.push({ d, y: yOf(d) })

  const points = rows.map((row, i) => ({
    issue: row.issue,
    win: row.win,
    x: xOf(i),
    y: yOf(row.win),
    showLabel: i % step === 0 || (i === n - 1 && (n - 1) % step !== 0)
  }))

  return {
    W,
    H,
    pad,
    plotBottom: pad.t + plotH,
    yTicks,
    points,
    line: points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
  }
})

const openSheet = async (name) => {
  if (name === 'lottery') await syncGameVisibility()
  sheet.value = name
  if (name === 'lottery') openAcc.value = 'fav'
}

const toggleAcc = (key) => {
  openAcc.value = openAcc.value === key ? '' : key
}

const pickPeriod = (value) => {
  period.value = value
  sheet.value = ''
  loadDraws()
}

const pickLottery = (id) => {
  if (id === gameId.value) {
    sheet.value = ''
    return
  }
  saveGameTrendPick(gameId.value)
  setActiveGame(id)
  restoreGameTrendPick(id)
  liveDraws.value = []
  router.replace({ path: '/trend', query: { id } })
  sheet.value = ''
  loadDraws()
}

const pickPosition = (index) => {
  position.value = index
  trendKind.value = 'single'
  comboKey.value = ''
  extraKey.value = ''
  saveGameTrendPick(gameId.value)
  sheet.value = ''
}

const pickCombo = (key) => {
  comboKey.value = key
  trendKind.value = 'combo'
  extraKey.value = ''
  const range = COMBO_RANGE[key]
  if (range) position.value = range[0]
  saveGameTrendPick(gameId.value)
  sheet.value = ''
}

const pickExtra = (key) => {
  extraKey.value = key
  trendKind.value = 'extra'
  comboKey.value = ''
  saveGameTrendPick(gameId.value)
  sheet.value = ''
}

const trendPickByGame = {}

const saveGameTrendPick = (id) => {
  trendPickByGame[id] = {
    position: position.value,
    trendKind: trendKind.value,
    comboKey: comboKey.value,
    extraKey: extraKey.value
  }
}

const restoreGameTrendPick = (id) => {
  const routeExtra = route.query.extra != null && route.query.extra !== ''
    ? String(route.query.extra)
    : ''
  const saved = trendPickByGame[id]
  const setting = getTrendSetting(games.value.find((g) => g.id === id) || currentGame.value)
  if (saved) {
    const posOk = setting.positions.some((p) => p.index === saved.position)
    const comboOk = saved.trendKind !== 'combo' || (setting.combos || []).includes(saved.comboKey)
    const extraOk = saved.trendKind !== 'extra' || (setting.extras || []).some((sec) =>
      sec.items.some((it) => it.key === saved.extraKey)
    )
    position.value = posOk ? saved.position : 0
    trendKind.value = comboOk && extraOk ? saved.trendKind : 'single'
    comboKey.value = comboOk ? saved.comboKey : ''
    extraKey.value = extraOk ? saved.extraKey : ''
    applyRouteTrendExtra(routeExtra, setting)
    return
  }
  position.value = 0
  if (setting.key === 'P3P5') {
    trendKind.value = 'combo'
    comboKey.value = 'All_5_Complex'
    extraKey.value = ''
    applyRouteTrendExtra(routeExtra, setting)
    return
  }
  if (setting.key === 'K3') {
    trendKind.value = 'combo'
    comboKey.value = 'K3_All'
    extraKey.value = ''
    applyRouteTrendExtra(routeExtra, setting)
    return
  }
  if (setting.key === 'PK10') {
    trendKind.value = 'extra'
    extraKey.value = 'dt'
    comboKey.value = ''
    applyRouteTrendExtra(routeExtra, setting)
    return
  }
  if (setting.key === 'DLT') {
    trendKind.value = 'combo'
    comboKey.value = 'DLT_Front'
    extraKey.value = ''
    applyRouteTrendExtra(routeExtra, setting)
    return
  }
  if (setting.key === 'SSQ') {
    trendKind.value = 'combo'
    comboKey.value = 'SSQ_Red'
    extraKey.value = ''
    applyRouteTrendExtra(routeExtra, setting)
    return
  }
  trendKind.value = 'single'
  comboKey.value = ''
  extraKey.value = ''
  applyRouteTrendExtra(routeExtra, setting)
}

const applyRouteTrendExtra = (key, setting = trendSetting.value) => {
  if (!key) return
  const ok = (setting.extras || []).some((sec) => sec.items.some((it) => it.key === key))
  if (!ok) return
  trendKind.value = 'extra'
  extraKey.value = key
  comboKey.value = ''
}

const triggerTextDownload = (text, filename) => {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

const downloadDrawHistory = async () => {
  if (downloading.value) return
  downloading.value = true
  const group = currentGame.value?.group || ''
  const filename = downloadFileName.value
  try {
    try {
      const text = await lgwApi.downloadDrawNumero(gameId.value)
      const rows = parseDrawDownloadText(text)
      if (rows.length) {
        triggerTextDownload(buildDrawDownloadText(rows, group) + '\n', filename)
        return
      }
    } catch (_) {}
    const rows = newestFirst.value ? draws.value : [...draws.value].reverse()
    const fallback = buildDrawDownloadText(rows, group)
    if (fallback) triggerTextDownload(fallback + '\n', filename)
  } finally {
    downloading.value = false
  }
}

const syncKSize = () => {
  const pane = chartType.value === 'hist' ? histPaneRef.value : kPaneRef.value
  if (!pane || (chartType.value !== 'kline' && chartType.value !== 'hist')) return
  const svg = pane.querySelector('svg')
  const target = svg || pane
  const w = Math.floor(target.clientWidth || pane.clientWidth)
  const h = Math.floor(target.clientHeight || pane.clientHeight)
  if (w < 80 || h < 80) return
  if (Math.abs(w - kSize.value.w) < 2 && Math.abs(h - kSize.value.h) < 2) return
  kSize.value = { w, h }
}

const layoutLine = () => {
  const inner = innerRef.value
  const table = inner?.querySelector('table')
  if (!inner || !table || chartType.value !== 'line' || !showLine.value) {
    if (linePoints.value) linePoints.value = ''
    if (bsLinePoints.value) bsLinePoints.value = ''
    if (oeLinePoints.value) oeLinePoints.value = ''
    return
  }
  const thead = table.querySelector('thead')
  const theadH = thead ? thead.offsetHeight : 0
  const bodyH = Array.from(table.querySelectorAll('tbody tr:not(.stat-row)')).reduce(
    (sum, tr) => sum + tr.offsetHeight,
    0
  )
  const nextStyle = {
    top: `${theadH}px`,
    width: `${table.offsetWidth}px`,
    height: `${bodyH}px`
  }
  if (
    svgStyle.value.top !== nextStyle.top ||
    svgStyle.value.width !== nextStyle.width ||
    svgStyle.value.height !== nextStyle.height
  ) {
    svgStyle.value = nextStyle
  }
  const wrapRect = inner.getBoundingClientRect()
  const toPoints = (selector) => {
    const balls = table.querySelectorAll(`tbody tr:not(.stat-row) ${selector}`)
    return Array.from(balls)
      .map((el) => {
        const r = el.getBoundingClientRect()
        return `${(r.left - wrapRect.left + r.width / 2).toFixed(1)},${(r.top - wrapRect.top - theadH + r.height / 2).toFixed(1)}`
      })
      .join(' ')
  }

  if (isP3p5Complex.value || isK3Complex.value || isK3SumForm.value || isP3p5Triple.value || isP3p5Pair.value || isD3All.value || isD3Sum.value || isDltForm.value || isSsqForm.value) {
    if (bsLinePoints.value) bsLinePoints.value = ''
    if (oeLinePoints.value) oeLinePoints.value = ''
    if (linePoints.value) linePoints.value = ''
    return
  }

  bsLinePoints.value = ''
  oeLinePoints.value = ''
  const nextPoints = toPoints('.hit-ball')
  if (linePoints.value !== nextPoints) linePoints.value = nextPoints
}

const updateLine = () => {
  if (lineRaf) return
  lineRaf = requestAnimationFrame(() => {
    lineRaf = 0
    layoutLine()
  })
}

const updateKSize = () => {
  if (kRaf) return
  kRaf = requestAnimationFrame(() => {
    kRaf = 0
    syncKSize()
  })
}

watch(
  [displayRows, p3p5ComplexDisplayRows, p3p5TripleDisplayRows, p3p5PairDisplayRows, d3AllDisplayRows, d3SumDisplayRows, dltFormDisplayRows, ssqFormDisplayRows, k3ComplexDisplayRows, k3SumDisplayRows, showLine, showMiss, showStats, chartType, position, comboKey, ballSize, isMultiHit, isP3p5Complex, isP3p5Triple, isP3p5Pair, isD3All, isD3Sum, isDltForm, isSsqForm, isK3Complex, isK3SumForm],
  async () => {
    await nextTick()
    updateLine()
  }
)

watch(digitLen, (len) => {
  if (position.value >= len) position.value = 0
  if (trendKind.value === 'combo' && !comboOptions.value.includes(comboKey.value)) {
    trendKind.value = 'single'
    comboKey.value = ''
  }
  if (trendKind.value === 'extra' && !extraSections.value.some((sec) => sec.items.some((it) => it.key === extraKey.value))) {
    trendKind.value = 'single'
    extraKey.value = ''
  }
})

watch(chartType, async (type) => {
  if (type !== 'line' && sheet.value === 'settings') sheet.value = ''
  if ((type === 'kline' || type === 'hist') && !isPk10ExtraLine.value) {
    if (trendKind.value !== 'single') {
      trendKind.value = 'single'
      comboKey.value = ''
      extraKey.value = ''
      saveGameTrendPick(gameId.value)
    }
  }
  if (type === 'hist' && isPk10ExtraLine.value) {
    chartType.value = 'line'
    return
  }
  if (type !== 'kline' && type !== 'hist') return
  await nextTick()
  // 切到 K 线/直方图时多测两次，避开 pos-row 出现后的高度变化
  updateKSize()
  requestAnimationFrame(() => updateKSize())
})

watch(gameId, () => {
  liveDraws.value = []
  loadDraws()
  restoreGameTrendPick(gameId.value)
})

watch(() => route.query.extra, (key) => {
  if (key == null || key === '') return
  applyRouteTrendExtra(String(key))
})

onMounted(async () => {
  restoreGameTrendPick(gameId.value)
  loadDraws()
  await nextTick()
  updateLine()
  updateKSize()
  if (typeof ResizeObserver !== 'undefined') {
    if (scrollRef.value) {
      ro = new ResizeObserver(() => updateLine())
      ro.observe(scrollRef.value)
    }
    const onChartResize = () => updateKSize()
    if (kPaneRef.value) {
      kRo = new ResizeObserver(onChartResize)
      kRo.observe(kPaneRef.value)
      if (histPaneRef.value) kRo.observe(histPaneRef.value)
    } else if (histPaneRef.value) {
      kRo = new ResizeObserver(onChartResize)
      kRo.observe(histPaneRef.value)
    }
  }
  window.addEventListener('resize', updateLine)
  window.addEventListener('resize', updateKSize)
})

onUnmounted(() => {
  if (ro) ro.disconnect()
  if (kRo) kRo.disconnect()
  if (lineRaf) cancelAnimationFrame(lineRaf)
  if (kRaf) cancelAnimationFrame(kRaf)
  window.removeEventListener('resize', updateLine)
  window.removeEventListener('resize', updateKSize)
})
</script>

<style scoped>
.page-wrap {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 480px;
  height: 100vh;
  height: 100dvh;
  height: 100svh;
  max-height: 100vh;
  max-height: 100dvh;
  max-height: 100svh;
  margin: 0 auto;
  background: #fff;
  font-family: 'PingFang SC', 'Microsoft YaHei', Avenir, Helvetica, Arial, sans-serif;
  color: #333;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px 8px 10px;
  background: #fff;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
}

.pos-title {
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: none;
  color: #333;
  font-size: 13px;
  line-height: 28px;
  white-space: nowrap;
  cursor: default;
  user-select: none;
  pointer-events: none;
}

.chart-tabs {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex-shrink: 0;
}

.chart-tab {
  padding: 5px 10px;
  border: 1px solid #d0d3d8;
  border-radius: 4px;
  background: #fff;
  color: #888;
  font-size: 13px;
  line-height: 1.35;
  white-space: nowrap;
  cursor: pointer;
}

.chart-tab.on {
  border-color: #f42c4c;
  color: #f42c4c;
}

.gear-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: none;
  color: #f42c4c;
  cursor: pointer;
  flex-shrink: 0;
}

.gear-btn svg {
  width: 28px;
  height: 28px;
  display: block;
}

.dt-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 11px;
}

.dt-table th,
.dt-table td {
  border: 1px solid #ececec;
  text-align: center;
  vertical-align: middle;
  padding: 0;
  height: 32px;
}

.dt-table thead th {
  position: sticky;
  top: 0;
  z-index: 4;
  background: #f3f4f6;
  color: #666;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.15;
}

.dt-col-issue {
  width: 13%;
  max-width: 52px;
  color: #8a8f99;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  overflow: hidden;
  padding: 0 1px;
}

.dt-issue-text {
  display: inline-block;
  font-size: 9px;
  line-height: 1.1;
  white-space: nowrap;
  transform: scale(0.88);
  transform-origin: center center;
  max-width: 100%;
}

.dt-col-code {
  width: 48%;
  min-width: 0;
  overflow: hidden;
  padding: 0 1px;
}

.dt-code-scale {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 0;
  width: 100%;
  transform: scale(0.92);
  transform-origin: center center;
}

.dt-col-dt {
  width: 7.8%;
  min-width: 24px;
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  padding: 0 1px;
}

.dt-col-dt small {
  display: block;
  font-size: 8px;
  line-height: 1;
  color: #999;
  font-weight: 400;
}

.dt-col-dt.dragon {
  color: #f42c4c;
}

.dt-col-dt.tiger {
  color: #2b6de5;
}

.gy-col-sum {
  width: 88px;
  font-size: 11px;
  line-height: 1.2;
  padding: 0 2px;
}

.gy-col-bs,
.gy-col-oe {
  width: 44px;
  font-size: 13px;
  font-weight: 600;
}

.gy-col-bs.big,
.gy-col-oe.even {
  color: #f42c4c;
}

.gy-col-bs.small,
.gy-col-oe.odd {
  color: #2b6de5;
}

.ft-col-gufan,
.ft-col-bs,
.ft-col-oe {
  width: 12%;
  min-width: 36px;
  font-size: 13px;
}

.ft-col-gufan {
  color: #888;
  font-weight: 500;
}

.ft-col-bs,
.ft-col-oe {
  font-weight: 600;
}

.ft-col-bs.big,
.ft-col-oe.even {
  color: #f42c4c;
}

.ft-col-bs.small,
.ft-col-oe.odd {
  color: #2b6de5;
}

.f5-table .dt-col-issue {
  width: 22%;
  max-width: 82px;
  overflow: hidden;
}

.f5-table .dt-issue-text {
  display: inline-block;
  font-size: 10px;
  line-height: 1.1;
  white-space: nowrap;
  transform: scale(0.9);
  transform-origin: center center;
}

.f5-col-rank {
  width: 15.6%;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  padding: 2px 1px;
  white-space: nowrap;
  overflow: hidden;
}

.f5-bs,
.f5-oe {
  display: inline-block;
  min-width: 1em;
}

.f5-bs {
  margin-right: 3px;
}

.f5-bs.big,
.f5-oe.even {
  color: #f42c4c;
}

.f5-bs.small,
.f5-oe.odd {
  color: #2b6de5;
}

.dt-table tbody tr.alt {
  background: #fafafa;
}

.pk10-ball {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 17px;
  margin: 0;
  border-radius: 3px;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  vertical-align: middle;
  flex-shrink: 0;
  box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.12);
  -webkit-text-stroke: 0.35px rgba(0, 0, 0, 0.35);
  paint-order: stroke fill;
}

.table-scroll {
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.table-scroll::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.table-inner {
  position: relative;
}

.pos-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  padding: 4px 10px 10px;
  background: #fff;
  flex-shrink: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.pos-row::-webkit-scrollbar {
  display: none;
}

.pos-row-center {
  justify-content: center;
}

.pos-row-pk10 {
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 6px;
  overflow-x: visible;
}

.pos-row-line {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pos-row-line-center {
  justify-content: center;
}

.pos-tab {
  flex: 0 0 auto;
  min-width: 48px;
  padding: 4px 12px;
  border: 1px solid #d0d3d8;
  border-radius: 4px;
  background: #fff;
  color: #666;
  font-size: 13px;
  line-height: 1.35;
  cursor: pointer;
}

.pos-tab.on {
  border-color: #f42c4c;
  color: #f42c4c;
}

.k-pane {
  flex: 1;
  min-height: 0;
  padding: 0 4px 4px;
  background: #fff;
}

.k-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.k-y-label {
  fill: #999;
  font-size: 11px;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.k-x-label {
  fill: #b0b0b0;
  font-size: 9px;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.trend-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 11px;
}

.trend-table.wide {
  width: max-content;
  min-width: 100%;
  table-layout: auto;
}

.trend-table.wide .col-d {
  min-width: 28px;
}

.trend-table th,
.trend-table td {
  border: 1px solid #ececec;
  text-align: center;
  vertical-align: middle;
  padding: 0;
  height: 30px;
}

.trend-table thead th {
  position: sticky;
  z-index: 4;
  background: #f3f4f6;
  color: #666;
  font-size: 12px;
  font-weight: 600;
}

.trend-table thead tr:first-child th {
  top: 0;
  height: 26px;
  z-index: 5;
}

.trend-table thead tr:last-child th {
  top: 26px;
  height: 24px;
  font-size: 12px;
  font-weight: 500;
  color: #777;
}

.col-issue {
  width: 76px;
  color: #8a8f99;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}

.col-code {
  width: 88px;
  color: #8a8f99;
  letter-spacing: 0.5px;
}

.col-code.k3 {
  width: clamp(56px, 18vw, 70px);
  letter-spacing: 0;
  padding: 0 1px;
}

.trend-table.k3 {
  font-size: clamp(10px, 2.8vw, 11px);
}

.trend-table.k3 .col-issue {
  width: clamp(56px, 18vw, 76px);
  font-size: clamp(10px, 2.7vw, 11px);
}

.trend-table.k3 .col-code .mini-dice {
  width: clamp(14px, 4vw, 20px);
  height: clamp(14px, 4vw, 20px);
  margin: 0 clamp(0px, 0.35vw, 1.5px);
}

.trend-table.k3 .col-code .mdot {
  width: clamp(2.4px, 0.7vw, 3.4px);
  height: clamp(2.4px, 0.7vw, 3.4px);
}

.trend-table.k3 .col-code .mdot.lt { left: clamp(1.5px, 0.55vw, 2.4px); top: clamp(1.5px, 0.55vw, 2.4px); }
.trend-table.k3 .col-code .mdot.lm { left: clamp(1.5px, 0.55vw, 2.4px); }
.trend-table.k3 .col-code .mdot.lb { left: clamp(1.5px, 0.55vw, 2.4px); bottom: clamp(1.5px, 0.55vw, 2.4px); }
.trend-table.k3 .col-code .mdot.rt { right: clamp(1.5px, 0.55vw, 2.4px); top: clamp(1.5px, 0.55vw, 2.4px); }
.trend-table.k3 .col-code .mdot.rm { right: clamp(1.5px, 0.55vw, 2.4px); }
.trend-table.k3 .col-code .mdot.rb { right: clamp(1.5px, 0.55vw, 2.4px); bottom: clamp(1.5px, 0.55vw, 2.4px); }

.code-n {
  display: inline-block;
  min-width: 9px;
  margin: 0 1px;
}

.code-n.cur {
  color: #f42c4c;
  font-weight: 700;
}

.code-n.dlt-back {
  color: #2f6bff;
}

.col-code.dlt .code-n {
  min-width: 16px;
  font-variant-numeric: tabular-nums;
}

.mini-dice {
  position: relative;
  display: inline-block;
  width: 20px;
  height: 20px;
  margin: 0 1.5px;
  border-radius: 25%;
  background: linear-gradient(to bottom right, #e0e0e0, #fcfcfc, #e0e0e0);
  box-shadow: 1px 1px 2.5px rgba(0, 0, 0, 0.3);
  vertical-align: middle;
}

.mdot {
  position: absolute;
  width: 3.4px;
  height: 3.4px;
  border-radius: 50%;
  background: #222;
}

.mdot.red {
  background: #e11d2e;
}

.mdot.lt { left: 2.4px; top: 2.4px; }
.mdot.lm { left: 2.4px; top: 50%; transform: translateY(-50%); }
.mdot.lb { left: 2.4px; bottom: 2.4px; }
.mdot.rt { right: 2.4px; top: 2.4px; }
.mdot.rm { right: 2.4px; top: 50%; transform: translateY(-50%); }
.mdot.rb { right: 2.4px; bottom: 2.4px; }
.mdot.c { left: 50%; top: 50%; transform: translate(-50%, -50%); }

.col-span {
  letter-spacing: 0.5px;
}

.col-d {
  width: auto;
}

.trend-table:not(.wide) .col-d {
  width: calc((100% - var(--fix-w, 148px)) / var(--ball-cols, 6));
}

.trend-table.k3 .col-code {
  width: clamp(56px, 18vw, 70px);
}

.sort-caret {
  display: inline-block;
  width: 0;
  height: 0;
  margin-left: 2px;
  border-left: 3px solid transparent;
  border-right: 3px solid transparent;
  border-top: 4px solid #999;
  vertical-align: middle;
}

.sort-caret.up {
  border-top: 0;
  border-bottom: 4px solid #999;
}

.trend-table tbody tr.alt td {
  background: #f7f8fa;
}

.table-scroll.p3p5-scroll {
  overflow-x: hidden;
}

/* 仅大乐透号码走势：左右滑看全号码，期号/开奖号左侧固定 */
.table-scroll.dlt-h-scroll {
  overflow-x: auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x pan-y;
  overscroll-behavior-x: contain;
}

.trend-table.dlt.wide {
  --dlt-issue-w: 74px;
  --dlt-code-w: 150px;
  width: max-content !important;
  min-width: max-content;
  table-layout: auto !important;
  border-collapse: separate;
  border-spacing: 0;
}

.trend-table.dlt.wide .col-d {
  min-width: 26px;
  width: 26px;
  padding: 0 1px;
  font-size: 11px;
  box-sizing: border-box;
}

.trend-table.dlt.wide .col-issue,
.trend-table.dlt.wide .col-code {
  position: sticky;
  z-index: 3;
  background: #fff;
  box-sizing: border-box;
}

.trend-table.dlt.wide .col-issue {
  left: 0;
  width: var(--dlt-issue-w);
  min-width: var(--dlt-issue-w);
  max-width: var(--dlt-issue-w);
}

.trend-table.dlt.wide .col-code {
  left: var(--dlt-issue-w);
  width: var(--dlt-code-w);
  min-width: var(--dlt-code-w);
  max-width: var(--dlt-code-w);
  box-shadow: 4px 0 6px -4px rgba(0, 0, 0, 0.1);
}

.trend-table.dlt.wide .stat-label {
  position: sticky;
  left: 0;
  z-index: 3;
  width: calc(var(--dlt-issue-w) + var(--dlt-code-w));
  min-width: calc(var(--dlt-issue-w) + var(--dlt-code-w));
  max-width: calc(var(--dlt-issue-w) + var(--dlt-code-w));
  background: #f0f1f4 !important;
  box-shadow: 4px 0 6px -4px rgba(0, 0, 0, 0.1);
}

.trend-table.dlt.wide thead .col-issue,
.trend-table.dlt.wide thead .col-code {
  z-index: 10;
  background: #f3f4f6;
}

.trend-table.dlt.wide tbody tr.alt .col-issue,
.trend-table.dlt.wide tbody tr.alt .col-code {
  background: #f7f8fa;
}

.trend-table.dlt.wide .col-code.dlt {
  line-height: 1.25;
  padding: 2px 1px;
  white-space: normal;
}

.p3-first2-inner {
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.p3-first2-table {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 12px;
}

.p3-first2-table th,
.p3-first2-table td {
  border: 1px solid #ececec;
  text-align: center;
  vertical-align: middle;
  padding: 0 2px;
  height: 36px;
  box-sizing: border-box;
}

.p3-first2-table thead th {
  position: sticky;
  top: 0;
  z-index: 4;
  height: 34px;
  background: #f3f4f6;
  color: #666;
  font-size: 13px;
  font-weight: 600;
}

.p3-first2-table .col-issue {
  width: 15%;
  color: #8a8f99;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}

.p3-first2-table .col-code {
  width: 20%;
  color: #8a8f99;
  font-size: 12px;
}

.p3-first2-table .col-bs,
.p3-first2-table .col-oe,
.p3-first2-table .col-pc {
  width: 12%;
}

.p3-first2-table .col-sum,
.p3-first2-table .col-span,
.p3-first2-table .col-pair {
  width: 9%;
  color: #8a8f99;
  font-variant-numeric: tabular-nums;
}

.p3-first2-table .code-line {
  display: block;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.p3-first2-table .type-chip {
  display: inline-block;
  min-width: 1em;
  margin: 0 1px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

.p3-first2-table .type-chip.big,
.p3-first2-table .type-chip.even,
.p3-first2-table .type-chip.prime {
  color: #f42c4c;
}

.p3-first2-table .type-chip.small,
.p3-first2-table .type-chip.odd {
  color: #2f6bff;
}

.p3-first2-table .type-chip.composite {
  color: #9aa0a8;
}

.p3-first2-table .form-check {
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 3px;
  vertical-align: middle;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 13px 13px;
}

.p3-first2-table .form-check.zu3 {
  background-color: #29c114;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M4.7 9.2 1.8 6.3l1.1-1.1 1.8 1.8 4-4.1 1.1 1.1z'/%3E%3C/svg%3E");
}

.p3-first2-table .miss-num {
  color: #c5c8ce;
  font-size: 12px;
}

.p3-first2-table tbody tr.alt td {
  background: #f7f8fa;
}

.p3-first2-table .stat-label {
  color: #666;
  font-size: 11px;
  text-align: center;
}

.p3-first2-table .stat-dash,
.p3-first2-table .stat-cell {
  color: #666;
  font-size: 11px;
}

.p3-first3-inner {
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.p3-first3-table {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 12px;
}

.p3-first3-table th,
.p3-first3-table td {
  border: 1px solid #ececec;
  text-align: center;
  vertical-align: middle;
  padding: 0 2px;
  height: 36px;
  box-sizing: border-box;
}

.p3-first3-table thead th {
  position: sticky;
  top: 0;
  z-index: 4;
  height: 34px;
  background: #f3f4f6;
  color: #666;
  font-size: 13px;
  font-weight: 600;
}

.p3-first3-table .col-issue {
  width: 16%;
  color: #8a8f99;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}

.p3-first3-table .col-code {
  width: 22%;
  color: #8a8f99;
  font-size: 12px;
}

.p3-first3-table .col-bs,
.p3-first3-table .col-oe,
.p3-first3-table .col-pc {
  width: 14%;
}

.p3-first3-table .col-form {
  width: 6.5%;
}

.d3-all-table .col-issue {
  width: 14%;
}

.d3-all-table .col-code {
  width: 18%;
}

.d3-all-table .col-bs,
.d3-all-table .col-oe,
.d3-all-table .col-pc {
  width: 12%;
}

.d3-all-table .col-sum {
  width: 8%;
  color: #666;
  font-variant-numeric: tabular-nums;
}

.d3-all-table .col-form {
  width: 7%;
}

.d3-sum-inner {
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.d3-sum-table {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 12px;
}

.d3-sum-table th,
.d3-sum-table td {
  border: 1px solid #ececec;
  text-align: center;
  vertical-align: middle;
  padding: 0 1px;
  height: 34px;
  box-sizing: border-box;
}

.d3-sum-table thead th {
  position: sticky;
  z-index: 4;
  background: #f3f4f6;
  color: #666;
  font-size: 12px;
  font-weight: 600;
}

.d3-sum-table thead tr:first-child th {
  top: 0;
  height: 28px;
}

.d3-sum-table thead tr:last-child th {
  top: 28px;
  height: 28px;
  font-size: 11px;
  font-weight: 500;
}

.d3-sum-table .col-issue {
  width: 14%;
  color: #8a8f99;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}

.d3-sum-table .col-code {
  width: 16%;
  color: #8a8f99;
  font-size: 12px;
}

.d3-sum-table .col-group,
.d3-sum-table .col-sub,
.d3-sum-table .col-num {
  width: 8.75%;
}

.d3-sum-table .code-line {
  display: block;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.d3-sum-table .col-num {
  color: #666;
  font-variant-numeric: tabular-nums;
}

.d3-sum-table tbody tr.alt td {
  background: #fafafa;
}

.dlt-form-table {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 12px;
}

.dlt-form-table th,
.dlt-form-table td {
  border: 1px solid #e8e8e8;
  text-align: center;
  padding: 6px 4px;
  white-space: nowrap;
}

.dlt-form-table thead th {
  background: #f7f7f7;
  color: #666;
  font-weight: 600;
}

.dlt-form-table .col-issue {
  width: 72px;
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
}

.dlt-form-table .col-code {
  width: 210px;
  text-align: left;
  padding-left: 8px;
}

.dlt-form-table .col-num {
  min-width: 52px;
  color: #666;
  font-variant-numeric: tabular-nums;
}

.dlt-form-table tbody tr.alt td {
  background: #fafafa;
}

.dlt-form-table tbody tr.alt td.col-issue {
  background: #fafafa;
}

/* 形态表对齐前区：左右滑 + 期号/开奖号双列 sticky */
.dlt-form-table.dlt-form-wide {
  --dlt-issue-w: 74px;
  --dlt-code-w: 150px;
  width: max-content !important;
  min-width: max-content;
  table-layout: auto !important;
  border-collapse: separate;
  border-spacing: 0;
}
.dlt-form-table.dlt-form-wide .col-issue,
.dlt-form-table.dlt-form-wide .col-code {
  position: sticky;
  z-index: 3;
  background: #fff;
  box-sizing: border-box;
}
.dlt-form-table.dlt-form-wide .col-issue {
  left: 0;
  width: var(--dlt-issue-w);
  min-width: var(--dlt-issue-w);
  max-width: var(--dlt-issue-w);
}
.dlt-form-table.dlt-form-wide .col-code {
  left: var(--dlt-issue-w);
  width: var(--dlt-code-w);
  min-width: var(--dlt-code-w);
  max-width: var(--dlt-code-w);
  padding: 2px 4px;
  line-height: 1.25;
  white-space: normal;
  box-shadow: 4px 0 6px -4px rgba(0, 0, 0, 0.1);
}
.dlt-form-table.dlt-form-wide .col-num {
  min-width: 56px;
  padding: 6px 8px;
}
.dlt-form-table.dlt-form-wide thead .col-issue,
.dlt-form-table.dlt-form-wide thead .col-code {
  z-index: 10;
  background: #f3f4f6;
}
.dlt-form-table.dlt-form-wide tbody tr.alt .col-issue,
.dlt-form-table.dlt-form-wide tbody tr.alt .col-code {
  background: #f7f8fa;
}
.dlt-form-inner {
  width: max-content;
  min-width: 100%;
}

.p3-first3-table .code-line {
  display: block;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.p3-first3-table .type-chip {
  display: inline-block;
  min-width: 1em;
  margin: 0 1px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

.p3-first3-table .type-chip.big,
.p3-first3-table .type-chip.even,
.p3-first3-table .type-chip.prime {
  color: #f42c4c;
}

.p3-first3-table .type-chip.small,
.p3-first3-table .type-chip.odd {
  color: #2f6bff;
}

.p3-first3-table .type-chip.composite {
  color: #9aa0a8;
}

.p3-first3-table .form-check {
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 3px;
  vertical-align: middle;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 13px 13px;
}

.p3-first3-table .form-check.zu3 {
  background-color: #29c114;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M4.7 9.2 1.8 6.3l1.1-1.1 1.8 1.8 4-4.1 1.1 1.1z'/%3E%3C/svg%3E");
}

.p3-first3-table .form-check.zu6 {
  background-color: #f5a623;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M4.7 9.2 1.8 6.3l1.1-1.1 1.8 1.8 4-4.1 1.1 1.1z'/%3E%3C/svg%3E");
}

.p3-first3-table .form-check.baozi {
  background-color: #f42c4c;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M4.7 9.2 1.8 6.3l1.1-1.1 1.8 1.8 4-4.1 1.1 1.1z'/%3E%3C/svg%3E");
}

.p3-first3-table .miss-num {
  color: #c5c8ce;
  font-size: 12px;
}

.p3-first3-table tbody tr.alt td {
  background: #f7f8fa;
}

.p3-first3-table .stat-label {
  color: #666;
  font-size: 11px;
  text-align: center;
}

.p3-first3-table .stat-dash,
.p3-first3-table .stat-cell {
  color: #666;
  font-size: 11px;
}

.p3p5-complex-inner {
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.k3-complex-inner {
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.k3-sum-inner {
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.k3-sum-table {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: clamp(10px, 2.9vw, 12px);
}

.k3-sum-table th,
.k3-sum-table td {
  border: 1px solid #ececec;
  text-align: center;
  vertical-align: middle;
  padding: 0 1px;
  height: 36px;
  box-sizing: border-box;
  overflow: hidden;
}

.k3-sum-table thead th {
  position: sticky;
  top: 0;
  z-index: 4;
  height: 42px;
  background: #f3f4f6;
  color: #666;
  font-size: clamp(10px, 2.9vw, 12px);
  font-weight: 600;
  line-height: 1.15;
}

.k3-sum-table thead th small {
  display: block;
  font-size: clamp(8px, 2.4vw, 10px);
  font-weight: 500;
  color: #999;
}

.k3-sum-table .col-issue {
  width: 16%;
  color: #666;
  font-size: clamp(10px, 2.8vw, 12px);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.k3-sum-table .col-code {
  width: 20%;
  padding: 0;
}

.k3-sum-table .col-form {
  width: 14%;
}

.k3-sum-table .col-bsoe {
  width: 22%;
}

.k3-sum-table .col-code .mini-dice,
.k3-complex-table .col-code .mini-dice {
  width: clamp(13px, 4vw, 20px);
  height: clamp(13px, 4vw, 20px);
  margin: 0 clamp(0px, 0.4vw, 1.5px);
}

.k3-sum-table .col-code .mdot,
.k3-complex-table .col-code .mdot {
  width: clamp(2.2px, 0.7vw, 3.4px);
  height: clamp(2.2px, 0.7vw, 3.4px);
}

.k3-sum-table .col-code .mdot.lt,
.k3-complex-table .col-code .mdot.lt { left: clamp(1.4px, 0.55vw, 2.4px); top: clamp(1.4px, 0.55vw, 2.4px); }
.k3-sum-table .col-code .mdot.lm,
.k3-complex-table .col-code .mdot.lm { left: clamp(1.4px, 0.55vw, 2.4px); }
.k3-sum-table .col-code .mdot.lb,
.k3-complex-table .col-code .mdot.lb { left: clamp(1.4px, 0.55vw, 2.4px); bottom: clamp(1.4px, 0.55vw, 2.4px); }
.k3-sum-table .col-code .mdot.rt,
.k3-complex-table .col-code .mdot.rt { right: clamp(1.4px, 0.55vw, 2.4px); top: clamp(1.4px, 0.55vw, 2.4px); }
.k3-sum-table .col-code .mdot.rm,
.k3-complex-table .col-code .mdot.rm { right: clamp(1.4px, 0.55vw, 2.4px); }
.k3-sum-table .col-code .mdot.rb,
.k3-complex-table .col-code .mdot.rb { right: clamp(1.4px, 0.55vw, 2.4px); bottom: clamp(1.4px, 0.55vw, 2.4px); }

.k3-sum-table .form-check {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  position: relative;
  vertical-align: middle;
}

.k3-sum-table .form-check::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 4px;
  height: 8px;
  border-right: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: rotate(45deg);
}

.k3-sum-table .form-check.pair {
  background: #48d072;
}

.k3-sum-table .form-check.zu6 {
  background: #ffc076;
}

.k3-sum-table .form-check.baozi {
  background: #f42c4c;
}

.k3-sum-table .miss-num {
  color: #999;
  font-size: 12px;
}

.k3-sum-table .bsoe-chip {
  display: inline-block;
  margin: 0 clamp(0px, 0.4vw, 2px);
  font-size: clamp(11px, 3.2vw, 13px);
  font-weight: 600;
}

.k3-sum-table .bsoe-chip.big,
.k3-sum-table .bsoe-chip.even {
  color: #e11d48;
}

.k3-sum-table .bsoe-chip.small,
.k3-sum-table .bsoe-chip.odd {
  color: #2563eb;
}

.k3-sum-table tbody tr td {
  background: #e8e9ed;
}

.k3-sum-table tbody tr.alt td {
  background: #f2f3f5;
}

.k3-sum-table .stat-label {
  text-align: left;
  padding-left: clamp(4px, 1.5vw, 10px);
  color: #888;
  font-size: clamp(10px, 2.7vw, 11px);
  background: #fafafa;
}

.k3-sum-table .stat-cell,
.k3-sum-table .stat-dash {
  color: #666;
  font-size: 11px;
  background: #fafafa;
}

.k3-complex-table {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: clamp(10px, 2.9vw, 12px);
}

.k3-complex-table th,
.k3-complex-table td {
  border: 1px solid #ececec;
  text-align: center;
  vertical-align: middle;
  padding: 0 1px;
  height: 34px;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
}

.k3-complex-table thead th {
  position: sticky;
  top: 0;
  z-index: 4;
  height: 36px;
  background: #f3f4f6;
  color: #666;
  font-size: clamp(9px, 2.6vw, 12px);
  font-weight: 600;
  letter-spacing: -0.02em;
}

.k3-complex-table .col-issue {
  width: 17%;
  color: #666;
  font-size: clamp(10px, 2.8vw, 12px);
  text-overflow: ellipsis;
}

.k3-complex-table .col-code {
  width: 20%;
  letter-spacing: 0;
  padding: 0;
}

.k3-complex-table .col-ball,
.k3-complex-table .col-span-val,
.k3-complex-table .col-attr {
  width: 9%;
}

.k3-complex-table .col-span-val {
  color: #666;
  font-weight: 600;
}

.k3-complex-table .col-attr {
  font-size: clamp(11px, 3.2vw, 13px);
  font-weight: 600;
}

.k3-complex-table .col-attr.big,
.k3-complex-table .col-attr.even {
  color: #e11d48;
}

.k3-complex-table .col-attr.small,
.k3-complex-table .col-attr.odd {
  color: #2563eb;
}

.k3-complex-table tbody tr td {
  background: #e8e9ed;
}

.k3-complex-table tbody tr.alt td {
  background: #f2f3f5;
}

.k3-complex-table .stat-label {
  text-align: left;
  padding-left: clamp(4px, 1.5vw, 10px);
  color: #888;
  font-size: clamp(10px, 2.7vw, 11px);
  background: #fafafa;
}

.k3-complex-table .stat-cell,
.k3-complex-table .stat-dash {
  color: #666;
  font-size: 11px;
  background: #fafafa;
}

.k3-complex-table .stat-dash {
  color: #bbb;
}

.p3p5-complex-table {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 11px;
}

.p3p5-complex-table th,
.p3p5-complex-table td {
  border: 1px solid #ececec;
  text-align: center;
  vertical-align: middle;
  padding: 0;
  height: 34px;
  box-sizing: border-box;
}

.p3p5-complex-table thead th {
  position: sticky;
  z-index: 4;
  background: #f3f4f6;
  color: #666;
  font-size: 11px;
  font-weight: 600;
}

.p3p5-complex-table thead tr:first-child th {
  top: 0;
  height: 28px;
  z-index: 5;
}

.p3p5-complex-table thead tr:last-child th {
  top: 28px;
  height: 26px;
  font-size: 10px;
  font-weight: 500;
  color: #777;
}

.p3p5-complex-table .col-issue {
  width: 14%;
  color: #8a8f99;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}

.p3p5-complex-table .col-code {
  width: 17%;
  color: #8a8f99;
  font-size: 11px;
  letter-spacing: 0;
}

.p3p5-complex-table .col-sum {
  width: 7%;
  color: #8a8f99;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.p3p5-complex-table .col-group {
  font-size: 10px;
}

.p3p5-complex-table .col-ratio {
  width: 5.5%;
  font-size: 10px;
  overflow: hidden;
}

.p3p5-complex-table .code-line {
  display: block;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  white-space: nowrap;
}

.p3p5-complex-table .ratio-hit-label {
  position: relative;
  z-index: 2;
  display: block;
  width: 100%;
  height: 100%;
  min-height: 34px;
  line-height: 34px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  box-sizing: border-box;
}

.p3p5-complex-table .ratio-hit-bs {
  background: #b56adf;
}

.p3p5-complex-table .ratio-hit-oe {
  background: #29c114;
}

.p3p5-complex-table tbody tr.alt td {
  background: #f7f8fa;
}

.p3p5-complex-table .miss-num {
  font-size: 10px;
}

.miss-num {
  color: #c5c8ce;
  font-size: 11px;
  line-height: 1;
}

.hit-ball {
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ff7a18;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.stat-row td {
  background: #f0f1f4 !important;
  color: #666;
  font-size: 10px;
  height: 26px;
}

.stat-label {
  color: #555;
  font-size: 11px;
}

.trend-svg {
  position: absolute;
  left: 0;
  pointer-events: none;
  z-index: 1;
  overflow: visible;
}

.hist-pane {
  flex: 1;
  min-height: 0;
  padding: 8px 4px 10px;
  background: #fff;
}

.hist-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.hist-axis {
  fill: #999;
  font-size: 11px;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.fab {
  position: absolute;
  right: 12px;
  bottom: calc(48px + 14px + env(safe-area-inset-bottom, 0px));
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: #f42c4c;
  box-shadow: 0 4px 10px rgba(244, 44, 76, 0.35);
  cursor: pointer;
  z-index: 12;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fab svg {
  width: 22px;
  height: 22px;
}

.fab:disabled {
  opacity: 0.65;
  cursor: wait;
}

.foot-bar {
  position: relative;
  flex-shrink: 0;
  width: 100%;
  height: 48px;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  box-sizing: content-box;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  background: #fff;
  border-top: 1px solid #e6e6e6;
  z-index: 7;
}

.foot-bar button {
  border: 0;
  border-right: 1px solid #eee;
  background: #fff;
  color: #333;
  font-size: 13px;
  cursor: pointer;
}

.foot-bar button:last-child {
  border-right: 0;
}

.caret {
  display: inline-block;
  width: 0;
  height: 0;
  margin-left: 4px;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-bottom: 5px solid #999;
  vertical-align: 2px;
}

.mask {
  position: fixed;
  left: 50%;
  top: 0;
  bottom: 0;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  background: rgba(0, 0, 0, 0.4);
  z-index: 30;
  display: flex;
  align-items: flex-end;
}

.sheet {
  width: 100%;
  max-height: 70vh;
  overflow: auto;
  background: #fff;
  border-radius: 12px 12px 0 0;
  padding: 12px 0 16px;
}

.sheet-title {
  padding: 4px 16px 12px;
  font-size: 15px;
  font-weight: 700;
  text-align: center;
}

.sheet-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  border: 0;
  background: none;
  text-align: left;
  font-size: 14px;
  color: #333;
  cursor: pointer;
}

.sheet-item.on {
  color: #f42c4c;
}

.sheet-item img {
  width: 28px;
  height: 28px;
  border-radius: 6px;
}

.lot-name {
  flex: 1;
}

.lot-period {
  font-size: 12px;
  color: #999;
}

.check {
  color: #f42c4c;
  font-weight: 700;
}

.lottery-list {
  max-height: 52vh;
  overflow: auto;
}

.lot-mask {
  padding: 0;
  background: rgba(0, 0, 0, 0.45);
}

.lot-panel {
  width: 100%;
  max-height: 86vh;
  background: #f3f3f3;
  display: flex;
  flex-direction: column;
}

.lot-scroll {
  overflow: auto;
  flex: 1;
  min-height: 0;
}

.lot-acc + .lot-acc .lot-head {
  border-top: 1px solid #e6e6e6;
}

.lot-head {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 44px;
  padding: 0 40px;
  border: 0;
  background: #ececec;
  color: #333;
  font-size: 15px;
  cursor: pointer;
}

.lot-dice {
  width: 18px;
  height: 18px;
  margin-right: 4px;
  display: block;
  object-fit: contain;
  flex-shrink: 0;
}

.lot-chev {
  position: absolute;
  right: 10px;
  width: 22px;
  height: 22px;
  display: flex;
  transition: transform 0.18s;
}

.lot-chev.open {
  transform: rotate(180deg);
}

.lot-chev svg {
  width: 22px;
  height: 22px;
}

.lot-body {
  background: #fff;
}

.lot-game {
  display: block;
  width: 100%;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  color: #222;
  font-size: 14px;
  text-align: center;
  cursor: pointer;
}

.lot-game:last-child {
  border-bottom: 0;
}

.lot-game.on {
  color: #f42c4c;
}

.lot-cancel {
  flex-shrink: 0;
  width: 100%;
  height: 48px;
  border: 0;
  background: #f42c4c;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}

.set-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  font-size: 14px;
}

.set-label {
  padding: 8px 16px 6px;
  font-size: 12px;
  color: #999;
}

.pos-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 16px 8px;
}

.pos-chip {
  min-width: 56px;
  padding: 6px 10px;
  border: 1px solid #d0d3d8;
  border-radius: 4px;
  background: #fff;
  color: #666;
  font-size: 13px;
  cursor: pointer;
}

.pos-chip.on {
  border-color: #f42c4c;
  color: #f42c4c;
}

.switch {
  width: 42px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 99px;
  background: #d8d8d8;
  position: relative;
  cursor: pointer;
}

.switch::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.18s;
}

.switch.on {
  background: #f42c4c;
}

.switch.on::after {
  transform: translateX(18px);
}

.help-mask {
  align-items: flex-end;
  justify-content: stretch;
  padding: 0;
}

.filter-mask {
  align-items: center;
  justify-content: center;
  padding: 0 36px;
  background: rgba(0, 0, 0, 0.45);
}

.filter-dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 320px;
  max-height: 72vh;
  overflow: hidden;
  background: #fff;
  border-radius: 12px;
}

.filter-head {
  position: relative;
  flex-shrink: 0;
  height: 44px;
  background: #f42c4c;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  line-height: 44px;
  text-align: center;
}

.filter-x {
  position: absolute;
  top: 50%;
  right: 8px;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  transform: translateY(-50%);
  cursor: pointer;
}

.filter-x svg {
  display: block;
  width: 22px;
  height: 22px;
  margin: 0 auto;
}

.filter-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px 14px 20px;
}

.filter-sec + .filter-sec {
  margin-top: 18px;
}

.filter-sec h3 {
  margin: 0 0 10px;
  color: #666;
  font-size: 14px;
  font-weight: 600;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.filter-chip {
  position: relative;
  overflow: hidden;
  height: 36px;
  padding: 0 2px;
  border: 0;
  border-radius: 4px;
  background: #f2f2f2;
  color: #555;
  font-size: 13px;
  cursor: pointer;
}

.filter-chip.on {
  background: #feecee;
  color: #f42c4c;
}

.filter-chip.on::before {
  content: '';
  position: absolute;
  right: 0;
  bottom: 0;
  border-bottom: 16px solid #f42c4c;
  border-left: 16px solid transparent;
}

.filter-chip.on::after {
  content: '';
  position: absolute;
  right: 2px;
  bottom: 3px;
  width: 6px;
  height: 3px;
  border-left: 1.5px solid #fff;
  border-bottom: 1.5px solid #fff;
  transform: rotate(-45deg);
}

.help-dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 72vh;
  overflow: hidden;
  background: #fff;
  border-radius: 0;
  box-shadow: none;
}

.help-title {
  flex-shrink: 0;
  padding: 14px 16px;
  background: #ebebeb;
  color: #898989;
  font-size: 16px;
  text-align: center;
}

.help-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 14px 22px 10px 22px;
  background: #fff;
  font-size: 13px;
  line-height: 1.75;
  -webkit-overflow-scrolling: touch;
}

.help-sec {
  margin-bottom: 28px;
}

.help-sec:last-child {
  margin-bottom: 12px;
}

.help-sec h2 {
  margin: 0 0 12px;
  border-top: 1px solid #afafaf;
  border-bottom: 1px solid #afafaf;
  color: #111;
  font-size: 15px;
  font-weight: 600;
  line-height: 34px;
  text-align: center;
}

.help-sec-title {
  padding: 6px 0 2px;
  color: #f42c4c;
  font-size: 13px;
}

.help-sec-text {
  padding-left: 6px;
  color: #222;
}

.help-sub {
  margin: 4px 0 8px;
  padding-left: 28px;
  color: #222;
}

.help-sub li {
  list-style: disc;
}

.help-subtitle {
  padding: 4px 0;
  color: #222;
}

.help-subtitle span {
  margin: 0 1px;
  color: #f42c4c;
}

.help-close {
  flex-shrink: 0;
  width: 100%;
  height: 50px;
  border: 0;
  border-radius: 0;
  background: #f42c4c;
  color: #fff;
  font-size: 16px;
  line-height: 50px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.21);
}

.night {
  background: #000;
  color: #ddd;
}

.night .toolbar {
  background: #000;
  border-color: #222;
  color: #fff;
}

.night .pos-title {
  color: #fff;
}

.night .chart-tab {
  background: #000;
  border-color: #fff;
  color: #fff;
}

.night .chart-tab.on {
  border-color: #f42c4c;
  color: #f42c4c;
  background: #000;
}

.night .foot-bar,
.night .foot-bar button {
  background: #333;
  color: #fff;
  border-color: #444;
}

.night .caret {
  border-bottom-color: #fff;
}

.night .hist-pane,
.night .k-pane {
  background: #000;
}

.night .hist-axis {
  fill: #888;
}

.night .trend-table th,
.night .trend-table td {
  border-color: #3f424a;
  color: #ddd;
}

.night .trend-table thead th {
  background: #32343c;
  color: #bbb;
}

.night .trend-table tbody tr.alt td {
  background: #32343c;
}

.night .trend-table.dlt.wide .col-issue,
.night .trend-table.dlt.wide .col-code {
  background: #2c2e35;
}

.night .trend-table.dlt.wide thead .col-issue,
.night .trend-table.dlt.wide thead .col-code {
  background: #32343c;
}

.night .trend-table.dlt.wide tbody tr.alt .col-issue,
.night .trend-table.dlt.wide tbody tr.alt .col-code {
  background: #32343c;
}

.night .trend-table.dlt.wide .stat-label {
  background: #32343c !important;
}

.night .dlt-form-table th,
.night .dlt-form-table td {
  border-color: #3f424a;
  color: #ddd;
}
.night .dlt-form-table thead th {
  background: #32343c;
  color: #bbb;
}
.night .dlt-form-table tbody tr.alt td {
  background: #32343c;
}
.night .dlt-form-table.dlt-form-wide .col-issue,
.night .dlt-form-table.dlt-form-wide .col-code {
  background: #2c2e35;
}
.night .dlt-form-table.dlt-form-wide thead .col-issue,
.night .dlt-form-table.dlt-form-wide thead .col-code {
  background: #32343c;
}
.night .dlt-form-table.dlt-form-wide tbody tr.alt .col-issue,
.night .dlt-form-table.dlt-form-wide tbody tr.alt .col-code {
  background: #32343c;
}
.night .dlt-form-table .code-n {
  color: #ddd;
}
.night .dlt-form-table .code-n.dlt-back {
  color: #5b9dff;
}

.night .p3p5-complex-table th,
.night .p3p5-complex-table td {
  border-color: #3f424a;
  color: #ddd;
}

.night .p3p5-complex-table thead th {
  background: #32343c;
  color: #bbb;
}

.night .p3p5-complex-table tbody tr.alt td {
  background: #32343c;
}

.night .p3-first3-table th,
.night .p3-first3-table td {
  border-color: #3f424a;
  color: #ddd;
}

.night .p3-first3-table thead th {
  background: #32343c;
  color: #bbb;
}

.night .p3-first3-table tbody tr.alt td {
  background: #32343c;
}

.night .p3-first3-table .col-issue,
.night .p3-first3-table .col-code {
  color: #999;
}

.night .p3-first3-table .type-chip.big,
.night .p3-first3-table .type-chip.even,
.night .p3-first3-table .type-chip.prime {
  color: #ff6b7a;
}

.night .p3-first3-table .type-chip.small,
.night .p3-first3-table .type-chip.odd {
  color: #6b9fff;
}

.night .p3-first3-table .type-chip.composite,
.night .p3-first3-table .miss-num {
  color: #7a808a;
}

.night .p3-first3-table .stat-label,
.night .p3-first3-table .stat-dash,
.night .p3-first3-table .stat-cell {
  color: #bbb;
}

.night .d3-all-table .col-sum {
  color: #ccc;
}

.night .d3-sum-table th,
.night .d3-sum-table td {
  border-color: #3f424a;
}

.night .d3-sum-table thead th {
  background: #2c2e35;
  color: #aaa;
}

.night .d3-sum-table tbody tr.alt td {
  background: #32343c;
}

.night .d3-sum-table .col-issue,
.night .d3-sum-table .col-code,
.night .d3-sum-table .col-num {
  color: #ccc;
}

.night .p3-first2-table th,
.night .p3-first2-table td {
  border-color: #3f424a;
  color: #ddd;
}

.night .p3-first2-table thead th {
  background: #32343c;
  color: #bbb;
}

.night .p3-first2-table tbody tr.alt td {
  background: #32343c;
}

.night .p3-first2-table .col-issue,
.night .p3-first2-table .col-code,
.night .p3-first2-table .col-sum,
.night .p3-first2-table .col-span {
  color: #999;
}

.night .p3-first2-table .type-chip.big,
.night .p3-first2-table .type-chip.even,
.night .p3-first2-table .type-chip.prime {
  color: #ff6b7a;
}

.night .p3-first2-table .type-chip.small,
.night .p3-first2-table .type-chip.odd {
  color: #6b9fff;
}

.night .p3-first2-table .type-chip.composite,
.night .p3-first2-table .miss-num {
  color: #7a808a;
}

.night .p3-first2-table .stat-label,
.night .p3-first2-table .stat-dash,
.night .p3-first2-table .stat-cell {
  color: #bbb;
}

.night .dt-table th,
.night .dt-table td {
  border-color: #3f424a;
  color: #ddd;
}

.night .dt-table thead th {
  background: #32343c;
  color: #bbb;
}

.night .dt-table tbody tr.alt {
  background: #32343c;
}

.night .dt-col-issue {
  color: #999;
}

.night .dt-col-dt.dragon {
  color: #ff6b7a;
}

.night .dt-col-dt.tiger {
  color: #6b9fff;
}

.night .gy-col-bs.big,
.night .gy-col-oe.even {
  color: #ff6b7a;
}

.night .gy-col-bs.small,
.night .gy-col-oe.odd {
  color: #6b9fff;
}

.night .f5-bs.big,
.night .f5-oe.even {
  color: #ff6b7a;
}

.night .f5-bs.small,
.night .f5-oe.odd {
  color: #6b9fff;
}

.night .stat-row td {
  background: #3a3d45 !important;
  color: #aaa;
}

.night .pos-row,
.night .pos-tab,
.night .sheet,
.night .help-dialog,
.night .filter-dialog {
  background: #2c2e35;
}

.night .filter-sec h3 {
  color: #bbb;
}

.night .filter-chip {
  background: #3a3d45;
  color: #ddd;
}

.night .filter-chip.on {
  background: #4a3036;
  color: #ff8a9a;
}

.night .help-title {
  background: #3a3d45;
  color: #bbb;
}

.night .help-body {
  background: #2c2e35;
}

.night .help-sec h2 {
  color: #eee;
  border-color: #666;
}

.night .help-sec-text,
.night .help-sub,
.night .help-subtitle {
  color: #ddd;
}
.night .lot-body,
.night .lot-game {
  background: #2c2e35;
  color: #ddd;
}

/* 天成快三走势：深灰 / 浅灰斑马纹（避免一黑一白） */
.night .k3-sum-table tbody tr td,
.night .k3-complex-table tbody tr td {
  background: #2c2e35;
  color: #ddd;
  border-color: #3a3d45;
}

.night .k3-sum-table tbody tr.alt td,
.night .k3-complex-table tbody tr.alt td {
  background: #3a3d45;
}

.night .k3-sum-table thead th,
.night .k3-complex-table thead th {
  background: #3a3d45;
  color: #ccc;
  border-color: #4a4e56;
}

.night .k3-sum-table .stat-label,
.night .k3-sum-table .stat-cell,
.night .k3-sum-table .stat-dash {
  background: #32343c;
  color: #aaa;
}

.night .k3-complex-table .issue {
  color: #999;
}

.night .pos-tab {
  border-color: #5a5e68;
}

.night .pos-tab.on {
  border-color: #f42c4c;
  color: #f42c4c;
}

.night .sheet-item,
.night .lot-game {
  color: #ddd;
}

.night .lot-game {
  border-color: #3f424a;
}

.night .sheet-item.on,
.night .lot-game.on {
  color: #f42c4c;
}
</style>
